import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Users, Calendar, Loader2, FileText, Package, Phone, CheckSquare,
  GripVertical, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, BarChart3,
  RefreshCw, Building2, Download, Sparkles, Plus, Search, Pencil, Check,
  ChevronDown, ChevronUp, Trash2, X, ExternalLink, Eye, FolderKanban, ArrowUp,
  CalendarClock, CheckCircle2, AlertTriangle, StickyNote, PhoneCall, Clock,
} from 'lucide-react';
import {
  format, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  startOfYear, endOfYear, addMonths, subMonths, addWeeks, subWeeks,
  addDays, subDays, addYears, subYears,
} from 'date-fns';
import AppHeader from '../components/AppHeader';
import { toast } from 'sonner';
import { API } from '../App';
import { AddCallToCrmModal } from '../components/AddCallToCrmModal';
import MyProjectsHubView from '../components/ProjectTracking/MyProjectsHubView';
import {
  parsePhocasSheet, _toPhocasPeriod, _normName, _normalizeSectorName, _computeSectorTotals,
  ExportModal, Chip, CallRow, OwnerGroup, QuotesDetailModal, PhocasUploadZone,
  _fmtCcy, BUS, BU_OPTIONS, matchPhocas,
} from '../components/MarketSectorView';
import CrmSalesReportModal from '../components/CrmSalesReportModal';
import TeamReportViewerModal from '../components/TeamReportViewerModal';
import { ContactNotesTasksModal } from '../components/ContactNotesTasksModal';
import ParsedOrderViewModal from '../components/ParsedOrderViewModal';
import { BulkScheduleCallModal } from '../components/BulkScheduleCallModal';
import SalesActivityHub from '../components/SalesActivityHub';
import { TaskDetailsModal } from '../components/NotificationBars';

const ORANGE = '#ee7203';

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);
const fmtCount = (n) => (n > 1000 ? '200+' : (n ?? 0).toString());
// The 8x8 phone-call figures are real totals (more than 1,000 a month is genuine), so they are shown exactly, never capped.
const fmtExact = (n) => (typeof n === 'number' ? n.toLocaleString('en-GB') : (n ?? 0).toString());
const toISO    = (d) => d.toISOString().split('T')[0] + 'T00:00:00';
const toISOEnd = (d) => d.toISOString().split('T')[0] + 'T23:59:59';
const toDateStr = (d) => d.toISOString().split('T')[0];
const todayRange = () => { const d = new Date(); return { from: d, to: d }; };

const _isFutureDelivery = (deliveryDateStr, refDate) => {
  if (!deliveryDateStr || !refDate) return false;
  try {
    const parts = String(deliveryDateStr).trim().split('/');
    if (parts.length !== 3) return false;
    const ddYear = parseInt(parts[2], 10);
    const ddMonth = parseInt(parts[1], 10);
    if (isNaN(ddYear) || isNaN(ddMonth)) return false;
    const refYear = refDate.getFullYear();
    const refMonth = refDate.getMonth() + 1;
    return ddYear > refYear || (ddYear === refYear && ddMonth > refMonth);
  } catch { return false; }
};
const _splitByDelivery = (items, refDate) => {
  const current = [], future = [];
  (items || []).forEach(i => (_isFutureDelivery(i.delivery_date, refDate) ? future : current).push(i));
  return { current, future };
};

const sumStats = (statsList) => {
  const base = {
    quotes_count: 0, quotes_value: 0, quotes_amended_count: 0, quotes_amended_value: 0,
    orders_count: 0, orders_value: 0, orders_future_count: 0, orders_future_value: 0,
    crm_quotes_count: 0, crm_quotes_value: 0, crm_projects_count: 0, crm_projects_value: 0,
    calls_count: 0, tasks_count: 0,
    calls_8x8_outbound_count: 0, calls_8x8_inbound_count: 0, calls_8x8_logged_count: 0,
    tasks_today_count: 0, tasks_today_quote_value: 0,
    tasks_overdue_quotes_count: 0, tasks_overdue_quotes_value: 0,
    tasks_upcoming_quotes_count: 0, tasks_upcoming_quotes_value: 0,
  };
  for (const s of statsList) {
    if (!s) continue;
    for (const k of Object.keys(base)) base[k] += (s[k] || 0);
  }
  return base;
};

function StatBox({ icon: Icon, iconColor, label, count, value, split, splitLabel1 = 'Quotes', splitLabel2 = 'Projects', loading, onClickQuotes, onClickProjects, onClick, onClickAllCalls, ordersBreakdown, eightX8Breakdown }) {
  return (
    <div
      className={`bg-white rounded-2xl shadow-sm p-5 flex-1 min-w-[160px] flex flex-col gap-1.5 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      style={{ border: '2px solid #ee7203' }}
      onClick={split || ordersBreakdown || eightX8Breakdown ? undefined : onClick}
      data-testid={`stat-box-${label?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${iconColor}18` }}>
          <Icon className="w-4 h-4" style={{ color: iconColor }} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</span>
      </div>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-gray-300 mt-2" />
      ) : eightX8Breakdown ? (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-stretch gap-1.5 pb-1.5 mb-0.5 border-b border-gray-100">
            <div
              className={`flex-1 rounded-lg px-1 ${onClickAllCalls ? 'cursor-pointer hover:bg-orange-50 transition-colors' : ''}`}
              onClick={onClickAllCalls}
              data-testid="calls-all-row"
            >
              <p className="text-xs text-gray-400">8x8 Calls</p>
              <p className="text-2xl font-extrabold text-gray-900">{fmtExact(eightX8Breakdown.outbound + eightX8Breakdown.incoming)}</p>
            </div>
            <div
              className="flex-1 rounded-lg px-1 cursor-pointer hover:bg-emerald-50 transition-colors"
              onClick={eightX8Breakdown.onLogged}
              data-testid="calls-all-logged-row"
            >
              <p className="text-xs text-gray-400">Logged in CRM</p>
              <p className="text-2xl font-extrabold text-emerald-600">{fmtExact(eightX8Breakdown.logged)}</p>
            </div>
          </div>
          <div
            className="flex items-center justify-between rounded-lg px-1 py-1 cursor-pointer hover:bg-blue-50 transition-colors"
            onClick={eightX8Breakdown.onOutbound}
            data-testid="calls-8x8-outbound-row"
          >
            <span className="text-[11px] font-semibold text-gray-500">8x8 Outbound</span>
            <span className="text-sm font-bold text-gray-900">{fmtExact(eightX8Breakdown.outbound)}</span>
          </div>
          <div
            className="flex items-center justify-between rounded-lg px-1 py-1 cursor-pointer hover:bg-green-50 transition-colors"
            onClick={eightX8Breakdown.onIncoming}
            data-testid="calls-8x8-incoming-row"
          >
            <span className="text-[11px] font-semibold text-gray-500">8x8 Incoming</span>
            <span className="text-sm font-bold text-gray-900">{fmtExact(eightX8Breakdown.incoming)}</span>
          </div>
          <div
            className="flex items-center justify-between rounded-lg px-1 py-1 cursor-pointer hover:bg-emerald-50 transition-colors"
            onClick={eightX8Breakdown.onLogged}
            data-testid="calls-8x8-logged-row"
          >
            <span className="text-[11px] font-semibold text-gray-500">Logged in CRM</span>
            <span className="text-sm font-bold text-emerald-600">{fmtExact(eightX8Breakdown.logged)}</span>
          </div>
        </div>
      ) : ordersBreakdown ? (
        <div className={onClick ? 'cursor-pointer' : ''} onClick={onClick}>
          <div className="pb-1.5">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">This Month</p>
            <p className="text-2xl font-extrabold text-gray-900">{fmtCount(ordersBreakdown.thisMonth.count)}</p>
            {ordersBreakdown.thisMonth.value > 0 && (
              <p className="text-xs font-semibold" style={{ color: ORANGE }}>{fmtMoney(ordersBreakdown.thisMonth.value)}</p>
            )}
          </div>
          {ordersBreakdown.future.count > 0 && (
            <>
              <div className="border-t border-dashed border-gray-200 my-1" />
              <div className="pb-1.5">
                <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider">Future Months</p>
                <p className="text-2xl font-extrabold text-gray-900">{fmtCount(ordersBreakdown.future.count)}</p>
                {ordersBreakdown.future.value > 0 && (
                  <p className="text-xs font-semibold text-blue-500">{fmtMoney(ordersBreakdown.future.value)}</p>
                )}
              </div>
            </>
          )}
          {ordersBreakdown.future.count > 0 && (
            <div className="border-t border-gray-200 mt-1 pt-1.5">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total Placed</p>
              <p className="text-sm font-bold text-gray-700">
                {fmtCount(ordersBreakdown.thisMonth.count + ordersBreakdown.future.count)}
                {' · '}
                <span style={{ color: ORANGE }}>{fmtMoney(ordersBreakdown.thisMonth.value + ordersBreakdown.future.value)}</span>
              </p>
            </div>
          )}
        </div>
      ) : split ? (
        <>
          <div
            className={`border-b border-gray-100 pb-2 mb-1 rounded-lg px-1 ${onClickQuotes ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
            onClick={onClickQuotes}
          >
            <p className="text-xs text-gray-400">{splitLabel1}</p>
            <p className="text-2xl font-extrabold text-gray-900">{fmtCount(split.quotesCount)}</p>
            {split.quotesValue > 0 && <p className="text-xs font-semibold" style={{ color: ORANGE }}>{fmtMoney(split.quotesValue)}</p>}
          </div>
          <div
            className={`rounded-lg px-1 ${onClickProjects ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
            onClick={onClickProjects}
          >
            <p className="text-xs text-gray-400">{splitLabel2}</p>
            <p className="text-2xl font-extrabold text-gray-900">{fmtCount(split.projectsCount)}</p>
            {split.projectsValue > 0 && <p className="text-xs font-semibold" style={{ color: ORANGE }}>{fmtMoney(split.projectsValue)}</p>}
          </div>
        </>
      ) : (
        <>
          <p className="text-3xl font-extrabold text-gray-900">{fmtCount(count)}</p>
          {value !== undefined && value > 0 && (
            <p className="text-sm font-semibold" style={{ color: ORANGE }}>{fmtMoney(value)}</p>
          )}
        </>
      )}
    </div>
  );
}

function CRMTasksBox({ stats, loading, onClick }) {
  const todayCount    = stats?.tasks_today_count ?? 0;
  const todayVal      = stats?.tasks_today_quote_value ?? 0;
  const overdueCount  = stats?.tasks_overdue_quotes_count ?? 0;
  const overdueVal    = stats?.tasks_overdue_quotes_value ?? 0;
  const upcomingCount = stats?.tasks_upcoming_quotes_count ?? 0;
  const upcomingVal   = stats?.tasks_upcoming_quotes_value ?? 0;

  return (
    <div
      className="bg-white rounded-2xl shadow-sm p-4 flex-1 min-w-[200px] flex flex-col gap-0 cursor-pointer hover:shadow-md transition-shadow"
      style={{ border: '2px solid #9333ea' }}
      onClick={onClick}
      data-testid="crm-tasks-box"
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#9333ea18' }}>
          <CheckSquare className="w-4 h-4" style={{ color: '#9333ea' }} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">CRM Tasks</span>
      </div>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-gray-300 mt-2" />
      ) : (
        <div className="flex flex-col gap-1.5">
          <div className="rounded-xl px-3 py-2 hover:bg-purple-50 transition-colors" style={{ background: '#9333ea0a' }}>
            <p className="text-[10px] font-bold text-purple-500 uppercase tracking-wider mb-0.5">Today&apos;s Tasks</p>
            <div className="flex items-baseline gap-1.5">
              <p className="text-2xl font-extrabold text-gray-900">{fmtCount(todayCount)}</p>
              {todayVal > 0 && <p className="text-xs font-semibold" style={{ color: '#9333ea' }}>{fmtMoney(todayVal)}</p>}
            </div>
          </div>
          <div className="border-t border-dashed border-gray-100 my-0.5" />
          <div className="rounded-xl px-3 py-1.5" style={{ background: overdueCount > 0 ? '#ef44440a' : '#f9fafb' }}>
            <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: overdueCount > 0 ? '#dc2626' : '#9ca3af' }}>Overdue Quotes</p>
            <div className="flex items-baseline gap-1.5">
              <p className={`text-xl font-extrabold ${overdueCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>{fmtCount(overdueCount)}</p>
              {overdueVal > 0 && <p className="text-xs font-semibold text-red-500">{fmtMoney(overdueVal)}</p>}
            </div>
          </div>
          <div className="rounded-xl px-3 py-1.5" style={{ background: upcomingCount > 0 ? '#f597030a' : '#f9fafb' }}>
            <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: upcomingCount > 0 ? '#d97706' : '#9ca3af' }}>Next 7 Days</p>
            <div className="flex items-baseline gap-1.5">
              <p className={`text-xl font-extrabold ${upcomingCount > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{fmtCount(upcomingCount)}</p>
              {upcomingVal > 0 && <p className="text-xs font-semibold text-amber-500">{fmtMoney(upcomingVal)}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomListRow({ tabId, item, phocasNormLookup, phocasPrevNormLookup, phocasPeriodLabel, phocasPrevPeriodLabel, canEdit, callStatus, onUpdateItem, onRemove, onOpenCustomer, onAddCall, selected, onToggleSelect }) {
  const [isEditing, setIsEditing] = useState(false);
  const [noteVal, setNoteVal] = useState(item.notes || '');
  const [saving, setSaving] = useState(false);
  const [buSaving, setBuSaving] = useState(false);
  const [showPrevNotes, setShowPrevNotes] = useState(false);
  const [showAddPrompt, setShowAddPrompt] = useState(false);
  const taRef = useRef(null);

  const displayStatus = callStatus?.display_status;
  const isScheduledSoon = displayStatus === 'scheduled_soon';
  const isScheduledFar = displayStatus === 'scheduled_far';
  const isRecentlyCompleted = displayStatus === 'recently_completed';
  const isStale = displayStatus === 'stale';
  const eightX8Pending = !!callStatus?.eight_x8_pending;

  const previousNotes = useMemo(() => {
    const local = (item.notes_history || []).map(n => ({ ...n, source: n.source || 'quick_note' }));
    const crm = (callStatus?.previous_notes || []).map(n => ({ ...n, source: 'crm_call' }));
    return [...local, ...crm].sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 8);
  }, [item.notes_history, callStatus?.previous_notes]);

  const fmtStatusDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return ''; }
  };

  const startEdit = () => { setNoteVal(item.notes || ''); setIsEditing(true); setShowAddPrompt(false); setTimeout(() => taRef.current?.focus(), 50); };
  const cancelEdit = () => setIsEditing(false);
  const saveNote = async () => {
    setSaving(true);
    await onUpdateItem(item.id, { notes: noteVal });
    setSaving(false);
    setIsEditing(false);
  };
  const handleBuChange = async (newBu) => {
    setBuSaving(true);
    await onUpdateItem(item.id, { business_unit: newBu });
    setBuSaving(false);
  };

  const phocasMatch = phocasNormLookup ? matchPhocas(item.account_name, phocasNormLookup) : null;
  const phocasPrev = phocasPrevNormLookup ? matchPhocas(item.account_name, phocasPrevNormLookup) : null;
  const delta = phocasMatch && phocasPrev ? phocasMatch.value - phocasPrev.value : null;
  const deltaPos = delta !== null && delta >= 0;

  const rowClass = isScheduledSoon
    ? 'bg-lime-50/70 border-l-4 border-l-lime-500 hover:bg-lime-50'
    : isScheduledFar
    ? 'bg-purple-50/70 border-l-4 border-l-purple-400 hover:bg-purple-50'
    : isRecentlyCompleted
    ? 'bg-blue-50/70 border-l-4 border-l-blue-400 hover:bg-blue-50'
    : 'hover:bg-gray-50/50';

  return (
    <div className={`border-b border-gray-100 last:border-0 transition-colors ${rowClass}`} data-testid={`folder-row-${item.id}`}>
      {(isScheduledSoon || isScheduledFar) && (
        <div className="flex items-center gap-1.5 px-3 pt-2" data-testid={`folder-row-scheduled-tag-${item.id}`}>
          <CalendarClock className={`w-3 h-3 ${isScheduledSoon ? 'text-lime-700' : 'text-purple-600'}`} />
          <span className={`text-[10px] font-bold uppercase tracking-wide ${isScheduledSoon ? 'text-lime-800' : 'text-purple-700'}`}>
            Call Scheduled — {fmtStatusDate(callStatus.date)}{isScheduledSoon ? ' (within 7 days)' : ''}
          </span>
        </div>
      )}
      <div className="grid gap-3 px-3 py-3 items-start" style={{ gridTemplateColumns: '1.8fr 0.65fr 1.2fr 1.6fr' }}>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            {onToggleSelect && (
              <input
                type="checkbox"
                checked={!!selected}
                onChange={() => onToggleSelect(item)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-orange-500 focus:ring-orange-400 cursor-pointer flex-shrink-0"
                data-testid={`folder-select-checkbox-${item.id}`}
                title="Select for bulk call scheduling"
              />
            )}
            <button onClick={() => onOpenCustomer(item.account_name)}
              className="font-semibold text-sm text-blue-700 hover:text-blue-900 hover:underline truncate text-left block max-w-full">
              {item.account_name || '—'}
            </button>
            {eightX8Pending && (
              <span
                className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 flex-shrink-0"
                data-testid={`folder-row-8x8-pending-${item.id}`}
                title="A live 8x8 call for this account hasn't been logged to CRM yet"
              >
                <Clock className="w-2.5 h-2.5" /> 8x8 call waiting
              </span>
            )}
          </div>
          {item.contact_name && <p className="text-xs text-gray-700 truncate mt-0.5">{item.contact_name}</p>}
          <p className="text-[11px] text-gray-400 mt-0.5">Added by {(item.added_by || '').split('@')[0] || '—'}</p>
        </div>

        <div className="min-w-0 pt-0.5">
          <div className="relative inline-block">
            <select value={item.business_unit || ''} onChange={e => handleBuChange(e.target.value)} disabled={buSaving}
              className={`appearance-none text-xs font-semibold rounded-md px-2 py-1 pr-5 cursor-pointer border transition-colors focus:outline-none ${
                item.business_unit ? 'bg-sky-100 text-sky-700 border-sky-200' : 'bg-gray-100 text-gray-500 border-gray-200'
              } ${buSaving ? 'opacity-50' : ''}`}>
              {BU_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ChevronDown className="absolute right-1 top-1.5 w-3 h-3 pointer-events-none text-gray-400" />
          </div>
        </div>

        <div className="min-w-0 pt-0.5 space-y-1">
          {phocasMatch ? (
            <>
              {delta !== null && (
                <div className="flex items-center gap-1">
                  {deltaPos ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : <TrendingDown className="w-3 h-3 text-red-400" />}
                  <span className={`text-[11px] font-bold ${deltaPos ? 'text-emerald-600' : 'text-red-500'}`}>
                    {deltaPos ? '+' : ''}{_fmtCcy(delta)}
                  </span>
                  {phocasPrevPeriodLabel && <span className="text-[10px] text-gray-400">vs {phocasPrevPeriodLabel}</span>}
                </div>
              )}
              <div className="flex items-center gap-1">
                <span className="text-xs font-bold text-gray-900">{_fmtCcy(phocasMatch.value)}</span>
                {phocasPeriodLabel && <span className="text-[10px] text-gray-500">{phocasPeriodLabel}</span>}
              </div>
              {(phocasMatch.contribution > 0 || phocasMatch.contrib_pct > 0) && (
                <p className="text-[11px] text-gray-500">
                  {phocasMatch.contribution > 0 && <span className="font-medium text-gray-700">{_fmtCcy(phocasMatch.contribution)}</span>}
                  {phocasMatch.contrib_pct != null && ` ${phocasMatch.contrib_pct.toFixed(1)}%`}
                </p>
              )}
            </>
          ) : (
            <span className="text-xs text-gray-400 italic">No Phocas match</span>
          )}
        </div>

        <div className="min-w-0 relative group">
          {isRecentlyCompleted && (
            <div className="flex items-center gap-1.5 mb-1.5" data-testid={`folder-row-completed-tag-${item.id}`}>
              <CheckCircle2 className="w-3 h-3 text-blue-600" />
              <span className="text-[10px] font-bold uppercase tracking-wide text-blue-700">Call Complete — {fmtStatusDate(callStatus.date)}</span>
            </div>
          )}
          {isStale && (
            <div className="flex items-center gap-1 mb-1.5" data-testid={`folder-row-stale-warning-${item.id}`}>
              <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
              <span className="text-[10px] font-semibold text-red-600">
                Hasn't been called in {callStatus.days_since_last_call} day{callStatus.days_since_last_call === 1 ? '' : 's'}
              </span>
            </div>
          )}
          {isEditing ? (
            <div className="space-y-1.5">
              <textarea ref={taRef} value={noteVal} onChange={e => setNoteVal(e.target.value)} rows={3}
                placeholder="Type notes…"
                data-testid={`folder-row-note-textarea-${item.id}`}
                className="w-full rounded-lg border border-sky-300 bg-white text-xs text-gray-900 px-2 py-1.5 resize-y focus:outline-none focus:ring-2 focus:ring-sky-400" />
              <div className="flex items-center gap-1.5">
                <button onClick={saveNote} disabled={saving} data-testid={`folder-row-note-save-${item.id}`}
                  className="flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-md bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={cancelEdit} disabled={saving} data-testid={`folder-row-note-cancel-${item.id}`} className="text-xs text-gray-500 hover:text-gray-700 px-1">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              {item.notes ? (
                <div className="flex items-start gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-800 whitespace-pre-wrap">{item.notes}</p>
                </div>
              ) : callStatus?.note ? (
                <div className="flex items-start gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap italic">{callStatus.note}</p>
                    {callStatus.note_date && (
                      <p className="text-[10px] text-gray-400 mt-0.5">Last call — {fmtStatusDate(callStatus.note_date)}</p>
                    )}
                  </div>
                </div>
              ) : (
                <span className="text-xs text-gray-400 italic cursor-pointer hover:text-gray-600" onClick={() => setShowAddPrompt(true)}>
                  Click to add notes…
                </span>
              )}
              {previousNotes.length > 0 && (
                <div className="mt-1">
                  <button
                    onClick={() => setShowPrevNotes(v => !v)}
                    className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-gray-400 hover:text-gray-600"
                    data-testid={`folder-row-prev-notes-toggle-${item.id}`}
                  >
                    {showPrevNotes ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    Previous notes ({previousNotes.length})
                  </button>
                  {showPrevNotes && (
                    <div className="mt-1.5 space-y-1.5 pl-1 border-l-2 border-gray-100" data-testid={`folder-row-prev-notes-list-${item.id}`}>
                      {previousNotes.map((n, idx) => (
                        <div key={idx} className="pl-2">
                          <p className="text-[10px] font-semibold text-gray-400">
                            {fmtStatusDate(n.date)}{n.source === 'quick_note' ? ' · Quick note' : ' · CRM call'}
                          </p>
                          <p className="text-xs text-gray-600 whitespace-pre-wrap italic">{n.note}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="absolute -top-0.5 right-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="relative">
                  <button
                    onClick={() => setShowAddPrompt(v => !v)}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                    title="Add a new note or log a call"
                    data-testid={`folder-row-add-btn-${item.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {showAddPrompt && (
                    <div
                      className="absolute right-0 top-6 z-20 w-44 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden opacity-100"
                      data-testid={`folder-row-add-prompt-${item.id}`}
                    >
                      <button
                        onClick={startEdit}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-sky-50 hover:text-sky-700 text-left"
                        data-testid={`folder-row-add-quicknote-${item.id}`}
                      >
                        <StickyNote className="w-3.5 h-3.5" /> Quick Note
                      </button>
                      <button
                        onClick={() => { setShowAddPrompt(false); onAddCall?.(item); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-orange-50 hover:text-orange-700 text-left border-t border-gray-100"
                        data-testid={`folder-row-add-call-${item.id}`}
                      >
                        <PhoneCall className="w-3.5 h-3.5" /> Log a Call
                      </button>
                    </div>
                  )}
                </div>
                {canEdit && (
                  <button onClick={() => onRemove(item.id)} className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AddTabModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 50); }, []);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onCreate(name.trim());
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-extrabold text-gray-900">New Custom Tab</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <input ref={inputRef} type="text" value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="e.g. Chase List, Key Accounts…"
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 mb-4" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={!name.trim() || saving}
            className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50 transition-opacity"
            style={{ background: ORANGE }}>
            {saving ? 'Creating…' : 'Create Tab'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddAccountModal({ onClose, onAdd, existingIds }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) { setResults([]); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API}/zoho/search-accounts?q=${encodeURIComponent(query.trim())}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await r.json();
        setResults(d.accounts || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 400);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const handleAdd = async (acct) => {
    setAdding(acct.id);
    await onAdd({ account_id: acct.id, account_name: acct.name });
    setAdding(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: '80vh' }}>
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <h3 className="font-extrabold text-gray-900">Add Account</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 pt-3 pb-2">
          <div className="flex items-center gap-2 bg-gray-50 rounded-xl border border-gray-200 px-3 py-2">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} autoFocus
              placeholder="Search Zoho accounts by name…"
              className="flex-1 bg-transparent text-sm focus:outline-none text-gray-900 placeholder-gray-400" />
            {searching && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-4">
          {!query.trim() && (
            <p className="text-center text-sm text-gray-400 py-8">Start typing to search Zoho accounts…</p>
          )}
          {query.trim().length >= 2 && !searching && results.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-8">No accounts found for "{query}"</p>
          )}
          {results.map(acct => {
            const alreadyAdded = existingIds.has(acct.id);
            return (
              <div key={acct.id} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0 gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{acct.name}</p>
                  {acct.city && <p className="text-xs text-gray-400">{acct.city}{acct.country ? `, ${acct.country}` : ''}</p>}
                </div>
                <button onClick={() => handleAdd(acct)} disabled={alreadyAdded || adding === acct.id}
                  className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    alreadyAdded ? 'bg-gray-100 text-gray-400 cursor-default' :
                    'text-white hover:opacity-90'
                  }`}
                  style={alreadyAdded ? {} : { background: ORANGE }}>
                  {adding === acct.id ? <Loader2 className="w-3 h-3 animate-spin" /> : alreadyAdded ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  {alreadyAdded ? 'Added' : adding === acct.id ? '…' : 'Add'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CRMQuotesModal({ isOpen, onClose, quotes, loading, error, onOpenAccount }) {
  if (!isOpen) return null;
  const totalValue = quotes.reduce((sum, q) => sum + (parseFloat(q.value) || 0), 0);
  return (
    <div className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${ORANGE}18` }}>
              <FileText className="w-4 h-4" style={{ color: ORANGE }} />
            </div>
            <div>
              <p className="font-bold text-gray-900">CRM Quotes</p>
              <p className="text-xs text-gray-400">{quotes.length} quote{quotes.length !== 1 ? 's' : ''}{totalValue > 0 ? ` · ${fmtMoney(totalValue)} total` : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="crm-quotes-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-2.5">
          {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: ORANGE }} /></div>}
          {error && <p className="text-center text-amber-600 text-sm py-8">⚠ {error}</p>}
          {!loading && !error && quotes.length === 0 && (
            <div className="text-center py-16">
              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No quotes in this period</p>
            </div>
          )}
          {quotes.map(q => (
            <div key={q.id} data-testid={`quote-row-${q.id}`}
              className={`rounded-xl border p-4 transition-all ${q.account_id ? 'cursor-pointer hover:shadow-md hover:border-orange-300 border-gray-100' : 'border-gray-100'}`}
              onClick={() => q.account_id && onOpenAccount({ id: q.account_id, name: q.account_name })}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-bold text-gray-900 text-sm">{q.subject || '(No subject)'}</p>
                    {q.stage && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full flex-shrink-0">{q.stage}</span>}
                  </div>
                  {q.account_name && (
                    <div className="flex items-center gap-1 mb-1">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                      <p className="text-xs font-semibold text-gray-600 truncate">{q.account_name}</p>
                    </div>
                  )}
                  {q.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2 leading-relaxed">{q.description}</p>}
                </div>
                <div className="text-right flex-shrink-0 flex flex-col items-end gap-1.5">
                  {q.value != null && <p className="font-extrabold text-base" style={{ color: ORANGE }}>{fmtMoney(q.value)}</p>}
                  <a href={q.crm_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                    className="text-xs text-blue-500 hover:underline flex items-center gap-0.5">
                    <ExternalLink className="w-3 h-3" />CRM
                  </a>
                  {q.account_id && <span className="text-xs text-orange-500 font-medium">Open customer →</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CRMProjectsModal({ isOpen, onClose, projects, loading, error, onOpenAccount }) {
  if (!isOpen) return null;
  const totalValue = projects.reduce((sum, p) => sum + (parseFloat(p.value) || 0), 0);
  return (
    <div className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${ORANGE}18` }}>
              <Package className="w-4 h-4" style={{ color: ORANGE }} />
            </div>
            <div>
              <p className="font-bold text-gray-900">CRM Projects</p>
              <p className="text-xs text-gray-400">{projects.length} project{projects.length !== 1 ? 's' : ''}{totalValue > 0 ? ` · ${fmtMoney(totalValue)} total` : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="crm-projects-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: ORANGE }} /></div>}
          {error && <p className="text-center text-amber-600 text-sm py-8">⚠ {error}</p>}
          {!loading && !error && projects.length === 0 && (
            <div className="text-center py-16">
              <Package className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No projects in this period</p>
            </div>
          )}
          {projects.map(proj => (
            <div key={proj.id} className="rounded-xl border border-gray-100 overflow-hidden" data-testid={`project-row-${proj.id}`}>
              <div className="flex items-start justify-between gap-3 px-4 py-3 bg-gray-50/80 border-b border-gray-100">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <p className="font-bold text-gray-900 text-sm">{proj.name || '(No name)'}</p>
                    {proj.stage && <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full flex-shrink-0">{proj.stage}</span>}
                  </div>
                  {proj.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{proj.description}</p>}
                </div>
                <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                  {proj.value != null && <p className="font-extrabold text-base" style={{ color: ORANGE }}>{fmtMoney(proj.value)}</p>}
                  <a href={proj.crm_url} target="_blank" rel="noreferrer"
                    className="text-xs text-blue-500 hover:underline flex items-center gap-0.5">
                    <ExternalLink className="w-3 h-3" />CRM
                  </a>
                </div>
              </div>
              <div className="px-4 py-2.5 space-y-1.5 bg-white">
                {proj.account_id && (
                  <div className="flex items-center justify-between rounded-lg border border-orange-100 bg-orange-50/50 px-3 py-2 cursor-pointer hover:bg-orange-50 hover:border-orange-200 transition-colors"
                    onClick={() => onOpenAccount({ id: proj.account_id, name: proj.account_name })}
                    data-testid={`project-account-${proj.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                      <span className="text-sm font-semibold text-gray-800 truncate">{proj.account_name}</span>
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: `${ORANGE}18`, color: ORANGE }}>Project Account</span>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ORANGE }} />
                  </div>
                )}
                {proj.packages.map(pkg => (
                  <div key={pkg.id}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 transition-all ${pkg.account_id ? 'border-gray-100 cursor-pointer hover:border-orange-200 hover:bg-orange-50/30' : 'border-gray-100 bg-gray-50/40'}`}
                    onClick={() => pkg.account_id && onOpenAccount({ id: pkg.account_id, name: pkg.account_name })}
                    data-testid={`pkg-row-${pkg.id}`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${pkg.is_sub ? 'bg-purple-400' : 'bg-blue-400'}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{pkg.account_name || pkg.name || '—'}</p>
                        <p className="text-xs text-gray-400">{pkg.type}{pkg.status ? ` · ${pkg.status}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {pkg.amount != null && <p className="text-sm font-bold" style={{ color: ORANGE }}>{fmtMoney(pkg.amount)}</p>}
                      {pkg.account_id && <ExternalLink className="w-3 h-3 text-gray-300" />}
                    </div>
                  </div>
                ))}
                {!proj.account_id && proj.packages.length === 0 && (
                  <p className="text-xs text-gray-400 py-2 text-center italic">No customers linked to this project</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CRMTasksModal({ isOpen, onClose, tasks, loading, error, memberName, dateLabel }) {
  if (!isOpen) return null;
  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      const dt = new Date(d + 'T12:00:00');
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
    } catch { return d; }
  };
  const totalVal = tasks.reduce((s, t) => s + (parseFloat(t.quote_value) || 0), 0);
  const incomplete = tasks.filter(t => t.status !== 'Completed');
  const completed  = tasks.filter(t => t.status === 'Completed');

  const TaskRow = ({ t }) => (
    <div className={`rounded-xl border p-3 transition-all ${t.status === 'Completed' ? 'opacity-60 bg-gray-50 border-gray-100' : 'bg-white border-gray-100 hover:border-purple-200 hover:shadow-sm'}`}
      data-testid={`crm-task-row-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="font-bold text-gray-900 text-sm">{t.subject || '(No subject)'}</p>
            {t.status && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0 ${
                t.status === 'Completed' ? 'bg-green-100 text-green-700' : t.status === 'In Progress' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                {t.status}
              </span>
            )}
            {t.priority === 'High' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 uppercase">High</span>}
          </div>
          {(t.linked_account_name || t.quote_subject) && (
            <div className="flex items-center gap-1 mb-1">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#9333ea' }} />
              <p className="text-xs font-semibold text-gray-600 truncate">{t.linked_account_name || t.quote_subject}</p>
            </div>
          )}
          {t.quote_stage && <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">{t.quote_stage}</span>}
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {t.quote_value != null && parseFloat(t.quote_value) > 0 && (
            <p className="font-extrabold text-sm" style={{ color: ORANGE }}>{fmtMoney(t.quote_value)}</p>
          )}
          {t.due_date && <span className="text-[11px] text-gray-400">{fmtDate(t.due_date)}</span>}
          <a href={t.crm_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
            className="text-xs text-blue-500 hover:underline flex items-center gap-0.5">
            <ExternalLink className="w-3 h-3" />CRM
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#9333ea18' }}>
              <CheckSquare className="w-4 h-4" style={{ color: '#9333ea' }} />
            </div>
            <div>
              <p className="font-bold text-gray-900">CRM Tasks — {memberName}</p>
              <p className="text-xs text-gray-400">
                {tasks.length} task{tasks.length !== 1 ? 's' : ''}
                {totalVal > 0 ? ` · ${fmtMoney(totalVal)} in linked quotes` : ''}
                {dateLabel ? ` · ${dateLabel}` : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="crm-tasks-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-2">
          {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#9333ea' }} /></div>}
          {error && <p className="text-center text-amber-600 text-sm py-8">⚠ {error}</p>}
          {!loading && !error && tasks.length === 0 && (
            <div className="text-center py-16">
              <CheckSquare className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No tasks in this period</p>
            </div>
          )}
          {!loading && incomplete.length > 0 && (
            <>
              {incomplete.length !== tasks.length && <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1">Open ({incomplete.length})</p>}
              {incomplete.map(t => <TaskRow key={t.id} t={t} />)}
            </>
          )}
          {!loading && completed.length > 0 && (
            <>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1 mt-3">Completed ({completed.length})</p>
              {completed.map(t => <TaskRow key={t.id} t={t} />)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CallsDetailModal({ isOpen, onClose, calls, loading, error, memberName, dateLabel, onEditCall }) {
  if (!isOpen) return null;
  const fmtDateTime = (d) => {
    if (!d) return '—';
    try {
      const dt = new Date(d);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
    } catch { return d; }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0891b218' }}>
              <Phone className="w-4 h-4" style={{ color: '#0891b2' }} />
            </div>
            <div>
              <p className="font-bold text-gray-900">Calls Made — {memberName}</p>
              <p className="text-xs text-gray-400">{calls.length} call{calls.length !== 1 ? 's' : ''}{dateLabel ? ` · ${dateLabel}` : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="calls-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-2">
          {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#0891b2' }} /></div>}
          {error && <p className="text-center text-amber-600 text-sm py-8">⚠ {error}</p>}
          {!loading && !error && calls.length === 0 && (
            <div className="text-center py-16">
              <Phone className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No calls in this period</p>
            </div>
          )}
          {!loading && calls.map(c => (
            <div
              key={c.id}
              onClick={() => onEditCall?.(c)}
              className="rounded-xl border border-gray-100 p-3 hover:border-orange-300 hover:shadow-sm hover:bg-orange-50/30 transition-all bg-white cursor-pointer group"
              data-testid={`call-row-${c.id}`}
              title="Click to edit this call"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-bold text-gray-900 text-sm group-hover:text-orange-700 transition-colors">{c.subject || 'Call'}</p>
                    {c.call_type && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase ${c.call_type === 'Outbound' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{c.call_type}</span>
                    )}
                    {c.purpose && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{c.purpose}</span>}
                    {c.business_unit && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: '#ee720320', color: '#ee7203' }}>{c.business_unit}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    {c.contact_name && <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" /><span className="text-xs text-gray-600 font-medium">{c.contact_name}</span></div>}
                    {c.account_name && <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ORANGE }} /><span className="text-xs text-gray-600">{c.account_name}</span></div>}
                  </div>
                  {c.description && <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed mt-0.5">{c.description}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-xs text-gray-500 font-medium">{fmtDateTime(c.start_time)}</span>
                  {c.duration_mins > 0 && <span className="text-[11px] text-gray-400">{c.duration_mins} min{c.duration_mins !== 1 ? 's' : ''}</span>}
                  <a href={c.crm_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                    className="text-xs text-blue-500 hover:underline flex items-center gap-0.5">
                    <ExternalLink className="w-3 h-3" />CRM
                  </a>
                  <span className="text-[10px] text-gray-300 group-hover:text-orange-400 transition-colors flex items-center gap-0.5 mt-0.5">
                    <Pencil className="w-2.5 h-2.5" /> edit
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EightX8HistoryModal({ isOpen, onClose, title, calls, loading, error, showUser, onOpenAccount }) {
  if (!isOpen) return null;
  const fmtDateTime = (d) => {
    if (!d) return '—';
    try {
      const dt = new Date(d);
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
    } catch { return d; }
  };
  const byUser = showUser
    ? Object.entries(calls.reduce((acc, c) => {
        const name = c.user_name || 'Unknown';
        acc[name] = (acc[name] || 0) + 1;
        return acc;
      }, {})).sort((a, b) => b[1] - a[1])
    : [];
  return (
    <div className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0891b218' }}>
              <PhoneCall className="w-4 h-4" style={{ color: '#0891b2' }} />
            </div>
            <div>
              <p className="font-bold text-gray-900">{title}</p>
              <p className="text-xs text-gray-400">{calls.length} call{calls.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="8x8-history-modal-close"><X className="w-4 h-4" /></button>
        </div>
        {byUser.length > 1 && (
          <div className="flex flex-wrap gap-1.5 px-6 py-3 border-b border-gray-100 flex-shrink-0 bg-gray-50/60" data-testid="8x8-history-by-user-summary">
            {byUser.map(([name, n]) => (
              <span key={name} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-600" data-testid={`8x8-history-by-user-${name.toLowerCase().replace(/\s+/g, '-')}`}>
                {name} <span className="text-gray-900 font-bold">{n}</span>
              </span>
            ))}
          </div>
        )}
        <div className="overflow-y-auto flex-1 p-4 space-y-2">
          {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#0891b2' }} /></div>}
          {error && <p className="text-center text-amber-600 text-sm py-8">⚠ {error}</p>}
          {!loading && !error && calls.length === 0 && (
            <div className="text-center py-16">
              <PhoneCall className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No calls in this period</p>
            </div>
          )}
          {!loading && calls.map(c => (
            <div key={c.id} className="rounded-xl border border-gray-100 p-3 bg-white flex items-center gap-3" data-testid={`8x8-history-row-${c.id}`}>
              <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: c.direction === 'inbound' ? '#16a34a18' : '#2563eb18' }}>
                <PhoneCall className="w-3.5 h-3.5" style={{ color: c.direction === 'inbound' ? '#16a34a' : '#2563eb' }} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {c.account_id ? (
                    <button onClick={() => onOpenAccount({ id: c.account_id, name: c.account_name })}
                      className="text-sm font-bold text-gray-800 hover:text-blue-600 hover:underline truncate text-left"
                      data-testid={`8x8-history-account-${c.id}`}>
                      {c.account_name}
                    </button>
                  ) : (
                    <span className="text-sm font-bold text-gray-500 truncate">{c.account_name || 'Unknown number'}</span>
                  )}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase ${c.direction === 'inbound' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                    {c.direction === 'inbound' ? 'Incoming' : 'Outbound'}
                  </span>
                  {c.status === 'added' && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 uppercase">Logged</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
                  <span>{c.phone_number}</span>
                  {showUser && c.user_name && <span>· {c.user_name}</span>}
                </div>
              </div>
              <span className="text-xs text-gray-400 flex-shrink-0">{fmtDateTime(c.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LocalDrillModal({ isOpen, onClose, title, items, loading, error, onViewItem, isOrders, debugInfo, refDate }) {
  const [activeTab, setActiveTab] = useState('current');
  useEffect(() => { if (isOpen) setActiveTab('current'); }, [isOpen]);
  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
  };
  const { current: currentItems, future: futureItems } = isOrders ? _splitByDelivery(items, refDate) : { current: items, future: [] };
  const visibleItems = isOrders ? (activeTab === 'future' ? futureItems : currentItems) : items;
  const totalValue = items.reduce((s, i) => s + (i.orders_value || 0), 0);
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: isOrders ? '#16a34a18' : '#ee720318' }}>
              {isOrders ? <Package className="w-4 h-4" style={{ color: '#16a34a' }} /> : <FileText className="w-4 h-4" style={{ color: ORANGE }} />}
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{title}</h3>
              <p className="text-xs text-gray-400">{items.length} item{items.length !== 1 ? 's' : ''}{totalValue > 0 ? ` · ${new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP',maximumFractionDigits:0}).format(totalValue)} ex VAT` : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="local-drill-modal-close"><X className="w-4 h-4" /></button>
        </div>
        {isOrders && (
          <div className="flex items-center gap-2 px-6 py-2.5 border-b border-gray-100 flex-shrink-0">
            <button
              onClick={() => setActiveTab('current')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${activeTab === 'current' ? 'text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              style={activeTab === 'current' ? { background: ORANGE } : {}}
              data-testid="drill-tab-this-month"
            >
              This Month
              <span className={`px-1.5 rounded-full text-[10px] ${activeTab === 'current' ? 'bg-white/25' : 'bg-white'}`}>{currentItems.length}</span>
            </button>
            <button
              onClick={() => setActiveTab('future')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${activeTab === 'future' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              data-testid="drill-tab-future-months"
            >
              Future Months
              <span className={`px-1.5 rounded-full text-[10px] ${activeTab === 'future' ? 'bg-white/25' : 'bg-white'}`}>{futureItems.length}</span>
            </button>
          </div>
        )}
        {debugInfo && (
          <div className="px-6 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2 flex-shrink-0">
            <span className="text-[11px] font-semibold text-amber-700">Querying from_email:</span>
            <code className="text-[11px] bg-white border border-amber-200 rounded px-1.5 py-0.5 text-amber-800 font-mono">{debugInfo.stored_username || '—'}</code>
            <span className="text-[11px] text-amber-600 ml-1">({debugInfo.total_sample} match{debugInfo.total_sample !== 1 ? 'es' : ''} in DB)</span>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>}
          {error && <p className="text-sm text-red-500 py-4">{error}</p>}
          {!loading && !error && visibleItems.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">
              No {isOrders ? (activeTab === 'future' ? 'orders with delivery next month or later' : 'orders for delivery this month') : 'quotes'} found for this period
            </p>
          )}
          {!loading && visibleItems.map((item, i) => (
            <div key={item.id || i}
              className="flex items-center justify-between py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 rounded-xl px-2 -mx-2 transition-colors group"
              onClick={() => onViewItem(item)} data-testid={`drill-item-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-800 truncate">{item.customer_name || '—'}</p>
                  {item.order_number && item.order_number !== '—' && (
                    <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-mono flex-shrink-0">#{item.order_number}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-xs text-gray-400">{fmtDate(item.parsed_at)}</p>
                  {isOrders && item.delivery_date && (
                    <span className="text-[10px] font-semibold text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded-full flex-shrink-0">Del: {item.delivery_date}</span>
                  )}
                  {item.from_email && <span className="text-[10px] text-gray-300 font-mono truncate max-w-[200px]">{item.from_email}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {item.orders_value > 0 && (
                  <span className="text-sm font-bold" style={{ color: ORANGE }}>
                    {new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP',maximumFractionDigits:0}).format(item.orders_value)}
                  </span>
                )}
                <Eye className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AllTeamDrillModal({ isOpen, onClose, type, title, userGroups, loading, onViewItem, onOpenAccount, refDate }) {
  const isLocalType = type === 'orders' || type === 'quotes';
  const isCrmQuotesType = type === 'crm-quotes';
  const isOrdersType = type === 'orders';
  const [activeTab, setActiveTab] = useState('current');
  useEffect(() => { if (isOpen) setActiveTab('current'); }, [isOpen]);
  const fmtDateShort = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); } catch { return d; }
  };
  const getItemValue = (item) => isLocalType ? (item.orders_value || 0) : (parseFloat(item.value) || 0);
  const IconComp = type === 'orders' ? Package : type === 'crm-projects' ? Package : TrendingUp;
  const iconColor = type === 'orders' ? '#16a34a' : type === 'crm-quotes' ? '#2563eb' : type === 'crm-projects' ? '#9333ea' : ORANGE;
  const totalItems = userGroups.reduce((s, g) => s + g.items.length, 0);
  const totalValue = userGroups.reduce((s, g) => s + g.items.reduce((sv, i) => sv + getItemValue(i), 0), 0);

  const splitGroups = isOrdersType ? userGroups.map(g => ({ ...g, ..._splitByDelivery(g.items, refDate) })) : null;
  const tabCurrentCount = splitGroups ? splitGroups.reduce((s, g) => s + g.current.length, 0) : 0;
  const tabFutureCount = splitGroups ? splitGroups.reduce((s, g) => s + g.future.length, 0) : 0;
  const displayGroups = isOrdersType
    ? splitGroups.map(g => ({ ...g, items: activeTab === 'future' ? g.future : g.current }))
    : userGroups;

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${iconColor}18` }}>
              <IconComp className="w-4 h-4" style={{ color: iconColor }} />
            </div>
            <div>
              <p className="font-bold text-gray-900">{title} — All Team</p>
              <p className="text-xs text-gray-400">{totalItems} item{totalItems !== 1 ? 's' : ''}{totalValue > 0 ? ` · ${fmtMoney(totalValue)} total` : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="all-team-drill-modal-close"><X className="w-4 h-4" /></button>
        </div>
        {isOrdersType && (
          <div className="flex items-center gap-2 px-6 py-2.5 border-b border-gray-100 flex-shrink-0">
            <button
              onClick={() => setActiveTab('current')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${activeTab === 'current' ? 'text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              style={activeTab === 'current' ? { background: ORANGE } : {}}
              data-testid="all-drill-tab-this-month"
            >
              This Month
              <span className={`px-1.5 rounded-full text-[10px] ${activeTab === 'current' ? 'bg-white/25' : 'bg-white'}`}>{tabCurrentCount}</span>
            </button>
            <button
              onClick={() => setActiveTab('future')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${activeTab === 'future' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              data-testid="all-drill-tab-future-months"
            >
              Future Months
              <span className={`px-1.5 rounded-full text-[10px] ${activeTab === 'future' ? 'bg-white/25' : 'bg-white'}`}>{tabFutureCount}</span>
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: ORANGE }} /></div>}
          {!loading && displayGroups.map((group) => {
            const groupValue = group.items.reduce((sv, i) => sv + getItemValue(i), 0);
            return (
              <div key={group.uid} className="mb-5">
                <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-xl" style={{ background: `${ORANGE}12` }}>
                  <div className="w-7 h-7 rounded-full text-white text-xs flex items-center justify-center font-bold flex-shrink-0" style={{ background: ORANGE }}>
                    {(group.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <span className="font-bold text-sm text-gray-800">{group.name}</span>
                  <span className="text-xs text-gray-400">{group.items.length} item{group.items.length !== 1 ? 's' : ''}</span>
                  {groupValue > 0 && <span className="ml-auto text-sm font-bold" style={{ color: ORANGE }}>{fmtMoney(groupValue)}</span>}
                </div>
                {group.error ? (
                  <p className="text-xs text-red-500 pl-4 py-1">{group.error}</p>
                ) : group.items.length === 0 ? (
                  <p className="text-xs text-gray-400 pl-4 py-1 italic">No items in this period</p>
                ) : isLocalType ? (
                  <div className="pl-2">
                    {group.items.map((item, i) => (
                      <div key={item.id || i}
                        className="flex items-center justify-between py-2.5 border-b border-gray-50 cursor-pointer hover:bg-gray-50 rounded-xl px-2 -mx-2 transition-colors group"
                        onClick={() => onViewItem && onViewItem(item)} data-testid={`all-drill-item-${i}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-gray-800 truncate">{item.customer_name || '—'}</p>
                            {item.order_number && item.order_number !== '—' && (
                              <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-mono flex-shrink-0">#{item.order_number}</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400">{fmtDateShort(item.parsed_at)}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {item.orders_value > 0 && <span className="text-sm font-bold" style={{ color: ORANGE }}>{fmtMoney(item.orders_value)}</span>}
                          <Eye className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : isCrmQuotesType ? (
                  <div className="pl-2 space-y-1.5">
                    {group.items.map(q => (
                      <div key={q.id}
                        className={`rounded-xl border p-3 transition-all text-sm ${q.account_id ? 'cursor-pointer hover:shadow-md hover:border-orange-300 border-gray-100' : 'border-gray-100'}`}
                        onClick={() => q.account_id && onOpenAccount({ id: q.account_id, name: q.account_name })}
                        data-testid={`all-crm-quote-${q.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-gray-900">{q.subject || '(No subject)'}</p>
                            {q.account_name && <p className="text-xs text-gray-500 mt-0.5">{q.account_name}</p>}
                            {q.stage && <span className="mt-1 inline-block text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">{q.stage}</span>}
                          </div>
                          <div className="text-right flex-shrink-0">
                            {q.value != null && <p className="font-extrabold text-sm" style={{ color: ORANGE }}>{fmtMoney(q.value)}</p>}
                            <a href={q.crm_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                              className="text-xs text-blue-500 hover:underline flex items-center gap-0.5 justify-end mt-0.5">
                              <ExternalLink className="w-3 h-3" />CRM
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="pl-2 space-y-1.5">
                    {group.items.map(proj => (
                      <div key={proj.id} className="rounded-xl border border-gray-100 overflow-hidden" data-testid={`all-crm-proj-${proj.id}`}>
                        <div className="flex items-start justify-between gap-3 px-4 py-2.5 bg-gray-50/80">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-bold text-gray-900 text-sm">{proj.name || '(No name)'}</p>
                              {proj.stage && <span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded-full">{proj.stage}</span>}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            {proj.value != null && <p className="font-extrabold text-sm" style={{ color: ORANGE }}>{fmtMoney(proj.value)}</p>}
                            <a href={proj.crm_url} target="_blank" rel="noreferrer"
                              className="text-xs text-blue-500 hover:underline flex items-center gap-0.5 justify-end mt-0.5">
                              <ExternalLink className="w-3 h-3" />CRM
                            </a>
                          </div>
                        </div>
                        <div className="px-4 py-2 space-y-1 bg-white">
                          {proj.account_id && (
                            <div className="flex items-center justify-between rounded-lg border border-orange-100 bg-orange-50/50 px-3 py-1.5 cursor-pointer hover:bg-orange-50 transition-colors"
                              onClick={() => onOpenAccount({ id: proj.account_id, name: proj.account_name })}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                                <span className="text-xs font-semibold text-gray-800">{proj.account_name}</span>
                                <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: `${ORANGE}18`, color: ORANGE }}>Project</span>
                              </div>
                              <ExternalLink className="w-3 h-3" style={{ color: ORANGE }} />
                            </div>
                          )}
                          {(proj.packages || []).map(pkg => (
                            <div key={pkg.id}
                              className={`flex items-center justify-between rounded-lg border px-3 py-1.5 transition-all ${pkg.account_id ? 'border-gray-100 cursor-pointer hover:border-orange-200 hover:bg-orange-50/30' : 'border-gray-100 bg-gray-50/40'}`}
                              onClick={() => pkg.account_id && onOpenAccount({ id: pkg.account_id, name: pkg.account_name })}>
                              <div className="flex items-center gap-2">
                                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${pkg.is_sub ? 'bg-purple-400' : 'bg-blue-400'}`} />
                                <span className="text-xs font-semibold text-gray-700 truncate">{pkg.account_name || pkg.name || '—'}</span>
                              </div>
                              {pkg.amount != null && <span className="text-xs font-bold flex-shrink-0" style={{ color: ORANGE }}>{fmtMoney(pkg.amount)}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function SalesHubPage({ user, onLogout }) {
  const isManager = user?.role === 'super_admin' || user?.is_crm_manager || user?.is_team_manager || user?.can_view_team_management;

  const [hubMode, setHubMode] = useState('my');

  const [dateMode, setDateMode] = useState('day');
  const [dateRange, setDateRange] = useState(todayRange());
  const [picker, setPicker] = useState(null);
  const [calFrom, setCalFrom] = useState('');
  const [calTo, setCalTo] = useState('');
  const [pickerDate, setPickerDate] = useState(new Date());
  const pickerRef = useRef(null);

  const setDay = (d) => { setDateRange({ from: d, to: d }); setDateMode('day'); setPicker(null); };
  const setWeekFrom = (d) => { setDateRange({ from: startOfWeek(d, { weekStartsOn: 1 }), to: endOfWeek(d, { weekStartsOn: 1 }) }); setDateMode('week'); setPicker(null); };
  const setMonthFrom = (d) => { setDateRange({ from: startOfMonth(d), to: endOfMonth(d) }); setDateMode('month'); setPicker(null); };

  const handleModeClick = (mode) => {
    if (mode === 'day') { if (dateMode === 'day') { setPickerDate(dateRange.from); setPicker('day'); } else setDay(new Date()); }
    else if (mode === 'week') { if (dateMode === 'week') { setPickerDate(dateRange.from); setPicker('week'); } else setWeekFrom(new Date()); }
    else if (mode === 'month') { if (dateMode === 'month') { setPickerDate(dateRange.from); setPicker('month'); } else setMonthFrom(new Date()); }
    else if (mode === 'year') { const now = new Date(); setDateRange({ from: startOfYear(now), to: endOfYear(now) }); setDateMode('year'); setPicker(null); }
    else if (mode === 'calendar') { setCalFrom(dateRange.from.toISOString().split('T')[0]); setCalTo(dateRange.to.toISOString().split('T')[0]); setPicker('calendar'); }
  };

  const nav = (dir) => {
    if (dateMode === 'day') setDateRange(r => { const d = dir > 0 ? addDays(r.from, 1) : subDays(r.from, 1); return { from: d, to: d }; });
    else if (dateMode === 'week') setDateRange(r => { const d = dir > 0 ? addWeeks(r.from, 1) : subWeeks(r.from, 1); return { from: startOfWeek(d, { weekStartsOn: 1 }), to: endOfWeek(d, { weekStartsOn: 1 }) }; });
    else if (dateMode === 'month') setDateRange(r => { const d = dir > 0 ? addMonths(r.from, 1) : subMonths(r.from, 1); return { from: startOfMonth(d), to: endOfMonth(d) }; });
    else if (dateMode === 'year') setDateRange(r => { const d = dir > 0 ? addYears(r.from, 1) : subYears(r.from, 1); return { from: startOfYear(d), to: endOfYear(d) }; });
  };

  useEffect(() => {
    const h = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPicker(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const dateLabel = () => {
    if (dateMode === 'day') return format(dateRange.from, 'EEE dd MMM yyyy');
    if (dateMode === 'week') return `${format(dateRange.from, 'dd MMM')} – ${format(dateRange.to, 'dd MMM yyyy')}`;
    if (dateMode === 'month') return format(dateRange.from, 'MMMM yyyy');
    if (dateMode === 'year') return format(dateRange.from, 'yyyy');
    return `${format(dateRange.from, 'dd MMM')} – ${format(dateRange.to, 'dd MMM yyyy')}`;
  };

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [tabOrder, setTabOrder] = useState([]);
  const [activeMemberId, setActiveMemberId] = useState('all');

  useEffect(() => {
    (async () => {
      setTeamsLoading(true);
      try {
        const res = await fetch(`${API}/teams`, { headers: authHeaders() });
        if (res.status === 403) {
          setTeamsLoading(false);
          return;
        }
        const data = await res.json();
        let all = data.teams || [];
        if (user?.role !== 'super_admin') {
          all = all.filter(t => t.manager_user_id === user?.id || (t.member_user_ids || []).includes(user?.id));
        }
        setTeams(all);
        if (all.length > 0) { setSelectedTeamId(all[0].id); initTabs(all[0]); }
      } catch { /* silently ignore — teams are optional for My Hub */ }
      finally { setTeamsLoading(false); }
    })();
  }, []); // eslint-disable-line

  const initTabs = (team) => {
    const mgrId = team.manager_user_id || null;
    const uids = team.member_user_ids || [];
    const allIds = mgrId && !uids.includes(mgrId) ? [mgrId, ...uids] : [...uids];
    const saved = (() => { try { return JSON.parse(localStorage.getItem(`sh_order_${team.id}`) || '[]'); } catch { return []; } })();
    const ordered = saved.length
      ? [...saved.filter(id => allIds.includes(id)), ...allIds.filter(id => !saved.includes(id))]
      : allIds;
    setTabOrder(ordered);
    setActiveMemberId('all');
  };

  const handleTeamChange = (teamId) => {
    setSelectedTeamId(teamId);
    const team = teams.find(t => t.id === teamId);
    if (team) initTabs(team);
    setMemberStats({});
  };

  const selectedTeam = teams.find(t => t.id === selectedTeamId) || null;

  const memberName = (uid) => {
    if (!selectedTeam) return uid;
    if (uid === selectedTeam.manager_user_id) return selectedTeam.manager_name || uid;
    const idx = (selectedTeam.member_user_ids || []).indexOf(uid);
    return ((selectedTeam.member_names || [])[idx]) || uid;
  };

  const activeMemberName = activeMemberId === 'all' ? 'All Team' : memberName(activeMemberId);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const arr = Array.from(tabOrder);
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    setTabOrder(arr);
    if (selectedTeamId) localStorage.setItem(`sh_order_${selectedTeamId}`, JSON.stringify(arr));
  };

  const [memberStats, setMemberStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (hubMode === 'my') {
      (async () => {
        setStatsLoading(true);
        const from = encodeURIComponent(toISO(dateRange.from));
        const to = encodeURIComponent(toISOEnd(dateRange.to));
        try {
          const r = await fetch(`${API}/team-management/stats/${user.id}?from_date=${from}&to_date=${to}`, { headers: authHeaders() });
          const s = await r.json();
          setMemberStats({ [user.id]: s });
        } catch { setMemberStats({}); }
        finally { setStatsLoading(false); }
      })();
      return;
    }
    if (!selectedTeam) return;
    const uids = tabOrder.filter(uid => uid !== 'all');
    if (!uids.length) return;
    (async () => {
      setStatsLoading(true);
      const from = encodeURIComponent(toISO(dateRange.from));
      const to = encodeURIComponent(toISOEnd(dateRange.to));
      const results = await Promise.all(
        uids.map(uid =>
          fetch(`${API}/team-management/stats/${uid}?from_date=${from}&to_date=${to}`, { headers: authHeaders() })
            .then(r => r.json()).catch(() => null)
        )
      );
      const statsMap = {};
      uids.forEach((uid, i) => { statsMap[uid] = results[i]; });
      setMemberStats(statsMap);
      setStatsLoading(false);
    })();
  }, [hubMode, selectedTeamId, tabOrder.join(','), dateRange]); // eslint-disable-line

  const activeStats = useMemo(() => {
    if (hubMode === 'my') return memberStats[user.id] || null;
    if (activeMemberId === 'all') return sumStats(Object.values(memberStats));
    return memberStats[activeMemberId] || null;
  }, [memberStats, hubMode, activeMemberId, user.id]);

  const crmError = activeMemberId !== 'all' ? memberStats[activeMemberId]?.crm_error : null;

  // "Logged in CRM" in the Calls Made box = calls really logged in Zoho CRM (completed, with notes, started by now) for each
  // member over the chosen dates, from GET /8x8/crm-calls-logged — NOT the count of 8x8 calls flagged "added" (that missed every
  // call logged any other way: quick note, manual log, or straight in Zoho). Dates are the UK calendar days shown on screen.
  const [crmLogged, setCrmLogged] = useState({ counts: null, loading: false });
  const crmLoggedIds = hubMode === 'my' ? [user.id] : tabOrder.filter((uid) => uid !== 'all');
  useEffect(() => {
    if (!crmLoggedIds.length) { setCrmLogged({ counts: null, loading: false }); return undefined; }
    let cancelled = false;
    setCrmLogged((prev) => ({ ...prev, loading: true }));
    const params = new URLSearchParams({
      from_date: format(dateRange.from, 'yyyy-MM-dd'),
      to_date: format(dateRange.to, 'yyyy-MM-dd'),
      user_ids: crmLoggedIds.join(','),
    });
    fetch(`${API}/8x8/crm-calls-logged?${params.toString()}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setCrmLogged({ counts: d?.counts || null, loading: false }); })
      .catch(() => { if (!cancelled) setCrmLogged({ counts: null, loading: false }); });
    return () => { cancelled = true; };
  }, [hubMode, selectedTeamId, tabOrder.join(','), dateRange]); // eslint-disable-line
  const activeCrmLogged = (() => {
    const counts = crmLogged.counts;
    if (!counts) return null;
    if (hubMode === 'my') return counts[user.id] ?? 0;
    if (activeMemberId === 'all') return crmLoggedIds.reduce((sum, uid) => sum + (counts[uid] || 0), 0);
    return counts[activeMemberId] ?? 0;
  })();

  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsFetched, setCallsFetched] = useState(false);
  const [quotesData, setQuotesData] = useState(null);

  const fetchCalls = async () => {
    if (!selectedTeamId) { toast.error('No team selected'); return; }
    setCallsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        team_id: selectedTeamId,
        start_date: toDateStr(dateRange.from),
        end_date: toDateStr(dateRange.to),
      });
      const r = await fetch(`${API}/zoho/market-sector/calls?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      const newCalls = data.calls || [];
      setCalls(newCalls);
      setCallsFetched(true);

      const accountIds = [...new Set(newCalls.map(c => c.account_id).filter(Boolean))];
      if (accountIds.length) {
        try {
          const qp = new URLSearchParams({
            account_ids: accountIds.join(','),
            start_date: toDateStr(dateRange.from),
            end_date: toDateStr(dateRange.to),
          });
          const qr = await fetch(`${API}/zoho/market-sector/quotes-summary?${qp}`, { headers: { Authorization: `Bearer ${token}` } });
          const qdata = await qr.json();
          setQuotesData(qdata?.by_account || null);
        } catch { /* non-fatal */ }
      } else {
        setQuotesData(null);
      }

      toast.success(`Loaded ${newCalls.length} call${newCalls.length !== 1 ? 's' : ''}`);
    } catch { toast.error('Failed to load calls'); }
    finally { setCallsLoading(false); }
  };

  const [phocasData, setPhocasData] = useState(null);
  const [phocasExpanded, setPhocasExpanded] = useState(false);
  const phocasPeriodLabel = _toPhocasPeriod(toDateStr(dateRange.from));
  const phocasPrevPeriodLabel = _toPhocasPeriod(toDateStr(subMonths(dateRange.from, 1)));
  const [callsOnly, setCallsOnly] = useState(false);

  const prevFetchKey = useRef(null);
  useEffect(() => {
    if (!selectedTeamId) return;
    const key = `${selectedTeamId}|${toDateStr(dateRange.from)}|${toDateStr(dateRange.to)}`;
    if (prevFetchKey.current === key) return;
    prevFetchKey.current = key;
    (async () => {
      setCallsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ team_id: selectedTeamId, start_date: toDateStr(dateRange.from), end_date: toDateStr(dateRange.to) });
        const r = await fetch(`${API}/zoho/market-sector/calls?${params}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json();
        const newCalls = data.calls || [];
        setCalls(newCalls);
        setCallsFetched(true);
        const accountIds = [...new Set(newCalls.map(c => c.account_id).filter(Boolean))];
        if (accountIds.length) {
          try {
            const qp = new URLSearchParams({ account_ids: accountIds.join(','), start_date: toDateStr(dateRange.from), end_date: toDateStr(dateRange.to) });
            const qr = await fetch(`${API}/zoho/market-sector/quotes-summary?${qp}`, { headers: { Authorization: `Bearer ${token}` } });
            const qdata = await qr.json();
            setQuotesData(qdata?.by_account || null);
          } catch { /* non-fatal */ }
        } else {
          setQuotesData(null);
        }
      } catch { /* silently ignore auto-load errors */ }
      finally { setCallsLoading(false); }
    })();
  }, [selectedTeamId, dateRange]); // eslint-disable-line

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API}/zoho/phocas-data`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const pd = d?.data;
        if (pd?.byName) {
          pd.normLookup = {};
          Object.keys(pd.byName).forEach(n => { pd.normLookup[_normName(n)] = pd.byName[n]; });
          if (pd.prevByName) {
            pd.prevNormLookup = {};
            Object.keys(pd.prevByName).forEach(n => { pd.prevNormLookup[_normName(n)] = pd.prevByName[n]; });
          }
          if (pd.phocasSectors) pd.sectorTotals = _computeSectorTotals(pd.byName, pd.phocasSectors);
          setPhocasData(pd);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const handlePhocasParsed = useCallback(async (result, fileName) => {
    const withMeta = { ...result, fileName, savedAt: new Date().toISOString() };
    setPhocasData(withMeta);
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API}/zoho/phocas-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          byName: result.byName, prevByName: result.prevByName || {},
          phocasSectors: result.phocasSectors || {},
          rowCount: result.rowCount,
          periodLabel: result.periodLabel || '',
          prevPeriodLabel: result.prevPeriodLabel || '',
          fileName: fileName || '',
          prevYearByName: result.prevYearByName || {},
          currYearByName: result.currYearByName || {},
          productGroupsByName: result.productGroupsByName || {},
        }),
      });
    } catch { /* silently ignore */ }
  }, []); // eslint-disable-line

  const memberFilteredCalls = useMemo(() => {
    if (hubMode === 'my') {
      const myName = memberName(user.id);
      return calls.filter(c => {
        if (!c.owner_name) return false;
        const on = c.owner_name.toLowerCase();
        const mn = myName.toLowerCase();
        if (on === mn) return true;
        const emailPrefix = (user.username || user.email || '').split('@')[0].toLowerCase();
        return emailPrefix && on.includes(emailPrefix);
      });
    }
    if (activeMemberId === 'all') return calls;
    const mName = memberName(activeMemberId).toLowerCase();
    return calls.filter(c => (c.owner_name || '').toLowerCase() === mName);
  }, [calls, hubMode, activeMemberId, user]); // eslint-disable-line

  const [buFilters, setBuFilters] = useState(new Set());
  const [dirFilters, setDirFilters] = useState(new Set());
  const [scheduledOnly, setScheduledOnly] = useState(false);

  const toggleBu = (bu) => setBuFilters(prev => {
    const n = new Set(prev);
    if (n.size === 1 && n.has(bu)) { n.clear(); } else { n.clear(); n.add(bu); }
    return n;
  });
  const toggleDir = (dir) => setDirFilters(prev => { const n = new Set(prev); n.has(dir) ? n.delete(dir) : n.add(dir); return n; });

  const filteredCalls = useMemo(() => {
    let base = memberFilteredCalls;
    if (buFilters.size) base = base.filter(c => buFilters.has(c.business_unit || 'Unknown'));
    if (dirFilters.size) base = base.filter(c => dirFilters.has(c.direction || 'outgoing'));
    if (scheduledOnly) base = base.filter(c => (c.call_status || '').toLowerCase() === 'scheduled');
    return base;
  }, [memberFilteredCalls, buFilters, dirFilters, scheduledOnly]);

  const scheduledCallsCount = useMemo(() => {
    let base = memberFilteredCalls;
    if (buFilters.size) base = base.filter(c => buFilters.has(c.business_unit || 'Unknown'));
    if (dirFilters.size) base = base.filter(c => dirFilters.has(c.direction || 'outgoing'));
    return base.filter(c => (c.call_status || '').toLowerCase() === 'scheduled').length;
  }, [memberFilteredCalls, buFilters, dirFilters]);

  const buCounts = useMemo(() => BUS.reduce((acc, bu) => {
    acc[bu] = filteredCalls.filter(c => c.business_unit === bu).length; return acc;
  }, {}), [filteredCalls]);

  const outgoing = useMemo(() => filteredCalls.filter(c => c.direction !== 'incoming').length, [filteredCalls]);
  const incoming = useMemo(() => filteredCalls.filter(c => c.direction === 'incoming').length, [filteredCalls]);

  const byOwner = useMemo(() => filteredCalls.reduce((acc, c) => {
    const k = c.owner_name || 'Unknown';
    if (!acc[k]) acc[k] = [];
    acc[k].push(c);
    return acc;
  }, {}), [filteredCalls]);

  const owners = useMemo(() => Object.keys(byOwner).sort(), [byOwner]);

  const buStats = useMemo(() => {
    if (!callsOnly && phocasData?.sectorTotals && Object.keys(phocasData.sectorTotals).length > 0) {
      const result = {};
      BUS.forEach(bu => {
        const st = phocasData.sectorTotals[bu] || {};
        const buCalls = memberFilteredCalls.filter(c => c.business_unit === bu);
        const seenIds = new Set();
        let quotesCount = 0, quotesValue = 0, ordersCount = 0, ordersValue = 0;
        buCalls.forEach(c => {
          if (!c.account_id || seenIds.has(c.account_id)) return;
          seenIds.add(c.account_id);
          const qd = quotesData?.[c.account_id];
          if (qd) {
            quotesCount += qd.count || 0;
            quotesValue += qd.total_value || 0;
            (qd.quotes || []).forEach(q => {
              if ((q.stage || '').includes('Order') || (q.stage || '').includes('Won')) {
                ordersCount++;
                ordersValue += q.total || 0;
              }
            });
          }
        });
        result[bu] = { phocasTotal: st.value || 0, uniqueAccounts: st.count || 0, quotesCount, quotesValue, ordersCount, ordersValue };
      });
      return result;
    }
    const norm = phocasData?.normLookup;
    const result = {};
    BUS.forEach(bu => {
      const buCalls = memberFilteredCalls.filter(c => c.business_unit === bu);
      const seenIds = new Set();
      let phocasTotal = 0, quotesCount = 0, quotesValue = 0, ordersCount = 0, ordersValue = 0;
      buCalls.forEach(c => {
        if (!c.account_id || seenIds.has(c.account_id)) return;
        seenIds.add(c.account_id);
        const match = norm ? matchPhocas(c.account_name, norm) : null;
        if (match) phocasTotal += (match.value || 0);
        const qd = quotesData?.[c.account_id];
        if (qd) {
          quotesCount += qd.count || 0;
          quotesValue += qd.total_value || 0;
          (qd.quotes || []).forEach(q => {
            if ((q.stage || '').includes('Order') || (q.stage || '').includes('Won')) {
              ordersCount++;
              ordersValue += q.total || 0;
            }
          });
        }
      });
      result[bu] = { phocasTotal, quotesCount, quotesValue, ordersCount, ordersValue, uniqueAccounts: seenIds.size };
    });
    return result;
  }, [memberFilteredCalls, phocasData, quotesData, callsOnly]);

  const totalStats = useMemo(() => {
    if (!callsOnly && phocasData?.sectorTotals && Object.keys(phocasData.sectorTotals).length > 0) {
      let phocasTotal = 0;
      Object.values(phocasData.sectorTotals).forEach(st => { phocasTotal += st.value || 0; });
      const seenIds = new Set();
      let quotesCount = 0, quotesValue = 0;
      memberFilteredCalls.forEach(c => {
        if (!c.account_id || seenIds.has(c.account_id)) return;
        seenIds.add(c.account_id);
        const qd = quotesData?.[c.account_id];
        if (qd) { quotesCount += qd.count || 0; quotesValue += qd.total_value || 0; }
      });
      return { phocasTotal, quotesCount, quotesValue };
    }
    const norm = phocasData?.normLookup;
    const seenIds = new Set();
    let phocasTotal = 0, quotesCount = 0, quotesValue = 0;
    memberFilteredCalls.forEach(c => {
      if (!c.account_id || seenIds.has(c.account_id)) return;
      seenIds.add(c.account_id);
      const match = norm ? matchPhocas(c.account_name, norm) : null;
      if (match) phocasTotal += (match.value || 0);
      const qd = quotesData?.[c.account_id];
      if (qd) { quotesCount += qd.count || 0; quotesValue += qd.total_value || 0; }
    });
    return { phocasTotal, quotesCount, quotesValue };
  }, [memberFilteredCalls, phocasData, quotesData, callsOnly]);

  const buTotals = useMemo(() => {
    const result = {};
    BUS.forEach(bu => {
      result[bu] = {
        call_count: buCounts[bu] || 0,
        unique_accounts: (!callsOnly && phocasData?.sectorTotals?.[bu]?.count)
          ? phocasData.sectorTotals[bu].count
          : (buStats[bu]?.uniqueAccounts || 0),
        quotes_count: buStats[bu]?.quotesCount || 0,
        quotes_value: Math.round(buStats[bu]?.quotesValue || 0),
        orders_count: buStats[bu]?.ordersCount || 0,
        orders_value: Math.round(buStats[bu]?.ordersValue || 0),
        phocas_sales: Math.round(buStats[bu]?.phocasTotal || 0),
      };
    });
    return result;
  }, [buCounts, buStats, callsOnly, phocasData]);

  const teamName = selectedTeam?.team_name || '';

  const handleNoteUpdated = useCallback((callId, newNote) => {
    setCalls(prev => prev.map(c => c.id === callId ? { ...c, notes: newNote } : c));
  }, []);

  const handleBuUpdated = useCallback((callId, newBu) => {
    setCalls(prev => prev.map(c => c.id === callId ? { ...c, business_unit: newBu } : c));
  }, []);

  const [quotesModal, setQuotesModal] = useState(null);
  const handleOpenQuotes = useCallback((call) => {
    const info = quotesData ? (quotesData[call.account_id] ?? null) : null;
    setQuotesModal({ accountName: call.account_name, quotes: info?.quotes || [] });
  }, [quotesData]);

  const [customerModalName, setCustomerModalName] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [showCrmReport, setShowCrmReport] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const handleReportCreated = useCallback((report) => {
    setSelectedReport({ id: report.id, name: report.name });
  }, []);

  const [crmQuotesOpen, setCrmQuotesOpen] = useState(false);
  const [crmProjectsOpen, setCrmProjectsOpen] = useState(false);
  const [crmDetailItems, setCrmDetailItems] = useState([]);
  const [crmDetailLoading, setCrmDetailLoading] = useState(false);
  const [crmDetailError, setCrmDetailError] = useState(null);
  const [crmTasksOpen, setCrmTasksOpen] = useState(false);
  const [crmTasksItems, setCrmTasksItems] = useState([]);
  const [crmTasksLoading, setCrmTasksLoading] = useState(false);
  const [crmTasksError, setCrmTasksError] = useState(null);
  const [callsDetailOpen, setCallsDetailOpen] = useState(false);
  const [callsDetailItems, setCallsDetailItems] = useState([]);
  const [callsDetailLoading, setCallsDetailLoading] = useState(false);
  const [callsDetailError, setCallsDetailError] = useState(null);
  const [callsDetailEditingCall, setCallsDetailEditingCall] = useState(null);
  const [eightX8DetailOpen, setEightX8DetailOpen] = useState(false);
  const [eightX8DetailItems, setEightX8DetailItems] = useState([]);
  const [eightX8DetailLoading, setEightX8DetailLoading] = useState(false);
  const [eightX8DetailError, setEightX8DetailError] = useState(null);
  const [eightX8DetailTitle, setEightX8DetailTitle] = useState('');
  const [localOrdersOpen, setLocalOrdersOpen] = useState(false);
  const [localQuotesOpen, setLocalQuotesOpen] = useState(false);
  const [localDrillItems, setLocalDrillItems] = useState([]);
  const [localDrillLoading, setLocalDrillLoading] = useState(false);
  const [localDrillError, setLocalDrillError] = useState(null);
  const [parsedOrderView, setParsedOrderView] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [allTeamDrillOpen, setAllTeamDrillOpen] = useState(false);
  const [allTeamDrillType, setAllTeamDrillType] = useState('orders');
  const [allTeamDrillTitle, setAllTeamDrillTitle] = useState('');
  const [allTeamDrillGroups, setAllTeamDrillGroups] = useState([]);
  const [allTeamDrillLoading, setAllTeamDrillLoading] = useState(false);
  const [custModalOpen, setCustModalOpen] = useState(false);
  const [custModalAccount, setCustModalAccount] = useState(null);

  const [customLists, setCustomLists] = useState([]);
  const [activeListTab, setActiveListTab] = useState('activity');
  const [showAddTabModal, setShowAddTabModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);

  const [myProjects, setMyProjects] = useState([]);
  const [myProjectsLoading, setMyProjectsLoading] = useState(false);

  const fetchMyProjects = useCallback(async (targetUserId) => {
    setMyProjectsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = targetUserId === user.id
        ? `${API}/project-tracking/my-assigned`
        : `${API}/project-tracking/user-assigned/${targetUserId}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setMyProjects(data.projects || []);
    } catch {
      setMyProjects([]);
    } finally {
      setMyProjectsLoading(false);
    }
  }, [user.id]); // eslint-disable-line

  useEffect(() => {
    if (activeListTab !== 'projects') return;
    const targetId = hubMode === 'my' ? user.id : (activeMemberId !== 'all' ? activeMemberId : null);
    if (!targetId) return;
    fetchMyProjects(targetId);
  }, [activeListTab, hubMode, activeMemberId]); // eslint-disable-line

  const handleRemoveMyProject = async (projectId) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API}/project-tracking/my-assigned/${projectId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setMyProjects(prev => prev.filter(p => p.project_id !== projectId));
      toast.success('Project removed from your list');
    } catch { toast.error('Failed to remove project'); }
  };

  const handleManagerRemoveProject = async (userId, projectId) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API}/project-tracking/user-assigned/${userId}/${projectId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setMyProjects(prev => prev.filter(p => p.project_id !== projectId));
      toast.success('Project removed from member\'s list');
    } catch { toast.error('Failed to remove project'); }
  };

  useEffect(() => {
    if (!selectedTeamId) return;
    const token = localStorage.getItem('token');
    const targetMemberIdForLists = hubMode === 'my' ? user.id : activeMemberId;
    fetch(`${API}/zoho/custom-call-lists?team_id=${selectedTeamId}&member_id=${targetMemberIdForLists}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setCustomLists(d.tabs || []))
      .catch(() => {});
  }, [selectedTeamId, hubMode, activeMemberId]); // eslint-disable-line

  const handleCreateTab = async (tabName) => {
    const token = localStorage.getItem('token');
    try {
      const r = await fetch(`${API}/zoho/custom-call-lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          team_id: selectedTeamId,
          tab_name: tabName,
          ...(hubMode === 'team' && activeMemberId !== 'all' && activeMemberId !== user.id ? { owner_id: activeMemberId } : {}),
        }),
      });
      const data = await r.json();
      setCustomLists(prev => [...prev, data.tab]);
      setActiveListTab(data.tab.id);
      setShowAddTabModal(false);
      toast.success(`Tab "${tabName}" created`);
    } catch { toast.error('Failed to create tab'); }
  };

  const handleDeleteTab = async (tabId) => {
    if (!window.confirm('Delete this tab and all its accounts?')) return;
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API}/zoho/custom-call-lists/${tabId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setCustomLists(prev => prev.filter(t => t.id !== tabId));
      setActiveListTab('market');
      toast.success('Tab deleted');
    } catch { toast.error('Failed to delete tab'); }
  };

  const handleAddItem = async (item) => {
    if (activeListTab === 'market') return;
    const token = localStorage.getItem('token');
    try {
      const r = await fetch(`${API}/zoho/custom-call-lists/${activeListTab}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(item),
      });
      const data = await r.json();
      setCustomLists(prev => prev.map(t => t.id === activeListTab
        ? { ...t, items: [...(t.items || []), data.item] }
        : t
      ));
      toast.success(`${item.account_name} added`);
    } catch { toast.error('Failed to add account'); }
  };

  const handleRemoveItem = async (tabId, itemId) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API}/zoho/custom-call-lists/${tabId}/items/${itemId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setCustomLists(prev => prev.map(t => t.id === tabId
        ? { ...t, items: (t.items || []).filter(i => i.id !== itemId) }
        : t
      ));
    } catch { toast.error('Failed to remove account'); }
  };

  const handleUpdateItem = async (tabId, itemId, patch) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API}/zoho/custom-call-lists/${tabId}/items/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      setCustomLists(prev => prev.map(t => t.id === tabId
        ? { ...t, items: (t.items || []).map(i => {
            if (i.id !== itemId) return i;
            const oldNote = (i.notes || '').trim();
            const newNote = (patch.notes ?? i.notes ?? '').trim();
            const pushHistory = patch.notes !== undefined && oldNote && oldNote !== newNote;
            return {
              ...i, ...patch,
              notes_history: pushHistory
                ? [{ date: new Date().toISOString(), note: oldNote, source: 'quick_note' }, ...(i.notes_history || [])].slice(0, 8)
                : (i.notes_history || []),
            };
          }) }
        : t
      ));
    } catch { toast.error('Failed to update'); }
  };

  const activeCustomList = customLists.find(t => t.id === activeListTab) || null;
  const activeListExistingIds = useMemo(() => new Set((activeCustomList?.items || []).map(i => i.account_id)), [activeCustomList]);

  const [callStatusByAccount, setCallStatusByAccount] = useState({});
  const fetchCallStatus = useCallback(() => {
    if (!activeCustomList || activeListTab === 'market') { setCallStatusByAccount({}); return; }
    const accountIds = (activeCustomList.items || []).map(i => i.account_id).filter(Boolean);
    if (accountIds.length === 0) { setCallStatusByAccount({}); return; }
    const token = localStorage.getItem('token');
    fetch(`${API}/zoho/accounts/call-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ account_ids: accountIds }),
    }).then(r => r.json()).then(d => setCallStatusByAccount(d.statuses || {})).catch(() => setCallStatusByAccount({}));
  }, [activeCustomList, activeListTab]);
  useEffect(() => { fetchCallStatus(); }, [activeCustomList?.id, activeListTab, (activeCustomList?.items || []).length]); // eslint-disable-line
  useEffect(() => {
    window.addEventListener('c30:calls-changed', fetchCallStatus);
    return () => window.removeEventListener('c30:calls-changed', fetchCallStatus);
  }, [fetchCallStatus]);

  const [addCallItem, setAddCallItem] = useState(null);
  const [activityTaskModal, setActivityTaskModal] = useState(null);

  const effectiveMemberId = hubMode === 'my' ? user.id : activeMemberId;
  const isAllView = hubMode === 'team' && activeMemberId === 'all';
  const effectiveMemberName = hubMode === 'my'
    ? (user?.display_name || user?.username || 'Me')
    : (activeMemberId === 'all' ? 'All Team' : activeMemberName);

  const isSelfView = effectiveMemberId === user.id;
  const isManagerOfSelectedTeam = !!selectedTeam && selectedTeam.manager_user_id === user.id;
  const isOwnFolders = isSelfView || (hubMode === 'team' && !isAllView && isManagerOfSelectedTeam);

  const [selectedAccounts, setSelectedAccounts] = useState({});
  const [showBulkScheduleModal, setShowBulkScheduleModal] = useState(false);
  const toggleAccountSelect = (row) => {
    const accountId = row.account_id;
    if (!accountId) return;
    setSelectedAccounts(prev => {
      const next = { ...prev };
      if (next[accountId]) delete next[accountId];
      else next[accountId] = { account_id: accountId, account_name: row.account_name, contact_id: row.contact_id || null };
      return next;
    });
  };
  const selectedAccountsList = useMemo(() => Object.values(selectedAccounts), [selectedAccounts]);
  const selectedAccountIdsSet = useMemo(() => new Set(Object.keys(selectedAccounts)), [selectedAccounts]);
  const clearAccountSelection = () => setSelectedAccounts({});
  useEffect(() => { setSelectedAccounts({}); }, [activeListTab, effectiveMemberId]);

  const openCRMQuotes = async () => {
    setCrmDetailItems([]); setCrmDetailError(null); setCrmDetailLoading(true); setCrmQuotesOpen(true);
    try {
      const from = encodeURIComponent(toISO(dateRange.from));
      const to = encodeURIComponent(toISOEnd(dateRange.to));
      const r = await fetch(`${API}/team-management/crm-quotes/${effectiveMemberId}?from_date=${from}&to_date=${to}`, { headers: authHeaders() });
      if (!r.ok) { setCrmDetailError(`Failed to load (${r.status})`); return; }
      const data = await r.json();
      setCrmDetailItems(data.quotes || []);
      if (data.error) setCrmDetailError(data.error);
    } catch { setCrmDetailError('Failed to load quotes'); } finally { setCrmDetailLoading(false); }
  };

  const openCRMProjects = async () => {
    setCrmDetailItems([]); setCrmDetailError(null); setCrmDetailLoading(true); setCrmProjectsOpen(true);
    try {
      const from = encodeURIComponent(toISO(dateRange.from));
      const to = encodeURIComponent(toISOEnd(dateRange.to));
      const r = await fetch(`${API}/team-management/crm-projects/${effectiveMemberId}?from_date=${from}&to_date=${to}`, { headers: authHeaders() });
      if (!r.ok) { setCrmDetailError(`Failed to load (${r.status})`); return; }
      const data = await r.json();
      setCrmDetailItems(data.projects || []);
      if (data.error) setCrmDetailError(data.error);
    } catch { setCrmDetailError('Failed to load projects'); } finally { setCrmDetailLoading(false); }
  };

  const openCRMTasks = async () => {
    setCrmTasksItems([]); setCrmTasksError(null); setCrmTasksLoading(true); setCrmTasksOpen(true);
    try {
      const from = encodeURIComponent(toISO(dateRange.from));
      const to = encodeURIComponent(toISOEnd(dateRange.to));
      const r = await fetch(`${API}/team-management/crm-tasks/${effectiveMemberId}?from_date=${from}&to_date=${to}`, { headers: authHeaders() });
      if (!r.ok) { setCrmTasksError(`Failed to load (${r.status})`); return; }
      const data = await r.json();
      setCrmTasksItems(data.tasks || []);
      if (data.error) setCrmTasksError(data.error);
    } catch { setCrmTasksError('Failed to load tasks'); } finally { setCrmTasksLoading(false); }
  };

  const openCallsDetail = async () => {
    setCallsDetailItems([]); setCallsDetailError(null); setCallsDetailLoading(true); setCallsDetailOpen(true);
    try {
      const from = encodeURIComponent(toISO(dateRange.from));
      const to = encodeURIComponent(toISOEnd(dateRange.to));
      const r = await fetch(`${API}/team-management/calls-detail/${effectiveMemberId}?from_date=${from}&to_date=${to}`, { headers: authHeaders() });
      if (!r.ok) { setCallsDetailError(`Failed to load (${r.status})`); return; }
      const data = await r.json();
      setCallsDetailItems(data.calls || []);
      if (data.error) setCallsDetailError(data.error);
    } catch { setCallsDetailError('Failed to load calls'); } finally { setCallsDetailLoading(false); }
  };

  const openEightX8Detail = async (kind, title) => {
    setEightX8DetailItems([]); setEightX8DetailError(null); setEightX8DetailLoading(true);
    setEightX8DetailTitle(title); setEightX8DetailOpen(true);
    try {
      const params = new URLSearchParams({
        from_date: toDateStr(dateRange.from),
        to_date: toDateStr(dateRange.to),
      });
      if (!isAllView) params.set('user_id', effectiveMemberId);
      if (kind === 'outbound') params.set('direction', 'outbound');
      else if (kind === 'inbound') params.set('direction', 'inbound');
      else if (kind === 'logged') params.set('status', 'added');
      const r = await fetch(`${API}/8x8/calls-history?${params.toString()}`, { headers: authHeaders() });
      if (!r.ok) { setEightX8DetailError(`Failed to load (${r.status})`); return; }
      const data = await r.json();
      setEightX8DetailItems(data.calls || []);
    } catch { setEightX8DetailError('Failed to load calls'); } finally { setEightX8DetailLoading(false); }
  };

  // Click on "Logged in CRM": the calls behind that number, read from Zoho with the same rules as the count.
  const openCrmLoggedDetail = async () => {
    setEightX8DetailItems([]); setEightX8DetailError(null); setEightX8DetailLoading(true);
    setEightX8DetailTitle(isAllView ? 'Calls Logged in CRM — All Team' : `Calls Logged in CRM — ${activeMemberName}`);
    setEightX8DetailOpen(true);
    try {
      const ids = hubMode === 'my' ? [user.id] : (isAllView ? tabOrder.filter((uid) => uid !== 'all') : [effectiveMemberId]);
      const params = new URLSearchParams({
        from_date: format(dateRange.from, 'yyyy-MM-dd'),
        to_date: format(dateRange.to, 'yyyy-MM-dd'),
        user_ids: ids.join(','),
        include_calls: 'true',
      });
      const r = await fetch(`${API}/8x8/crm-calls-logged?${params.toString()}`, { headers: authHeaders() });
      if (!r.ok) { setEightX8DetailError(`Failed to load (${r.status})`); return; }
      const data = await r.json();
      setEightX8DetailItems(data.calls || []);
    } catch { setEightX8DetailError('Failed to load calls'); } finally { setEightX8DetailLoading(false); }
  };

  const openLocalOrders = async () => {
    setLocalDrillItems([]); setLocalDrillError(null); setLocalDrillLoading(true); setLocalOrdersOpen(true); setDebugInfo(null);
    try {
      const from = encodeURIComponent(toISO(dateRange.from));
      const to = encodeURIComponent(toISOEnd(dateRange.to));
      const [r, dbg] = await Promise.all([
        fetch(`${API}/team-management/local-orders/${effectiveMemberId}?from_date=${from}&to_date=${to}`, { headers: authHeaders() }),
        fetch(`${API}/team-management/debug/${effectiveMemberId}`, { headers: authHeaders() }).catch(() => null),
      ]);
      if (!r.ok) { setLocalDrillError(`Failed to load (${r.status})`); return; }
      const data = await r.json();
      setLocalDrillItems(data.orders || []);
      if (dbg && dbg.ok) { const d = await dbg.json(); setDebugInfo(d); }
    } catch { setLocalDrillError('Failed to load orders'); } finally { setLocalDrillLoading(false); }
  };

  const openLocalQuotes = async () => {
    setLocalDrillItems([]); setLocalDrillError(null); setLocalDrillLoading(true); setLocalQuotesOpen(true);
    try {
      const from = encodeURIComponent(toISO(dateRange.from));
      const to = encodeURIComponent(toISOEnd(dateRange.to));
      const r = await fetch(`${API}/team-management/local-quotes/${effectiveMemberId}?from_date=${from}&to_date=${to}`, { headers: authHeaders() });
      if (!r.ok) { setLocalDrillError(`Failed to load (${r.status})`); return; }
      const data = await r.json();
      setLocalDrillItems(data.quotes || []);
    } catch { setLocalDrillError('Failed to load quotes'); } finally { setLocalDrillLoading(false); }
  };

  const openAllTeamDrill = async (type, title, apiPath, itemsKey) => {
    setAllTeamDrillType(type);
    setAllTeamDrillTitle(title);
    setAllTeamDrillLoading(true);
    setAllTeamDrillGroups([]);
    setAllTeamDrillOpen(true);
    const from = encodeURIComponent(toISO(dateRange.from));
    const to = encodeURIComponent(toISOEnd(dateRange.to));
    const uids = tabOrder.filter(uid => uid !== 'all');
    const results = await Promise.allSettled(
      uids.map(uid =>
        fetch(`${API}/team-management/${apiPath}/${uid}?from_date=${from}&to_date=${to}`, { headers: authHeaders() })
          .then(r => r.json())
          .then(data => ({ uid, name: memberName(uid), items: data[itemsKey] || [], error: null }))
          .catch(() => ({ uid, name: memberName(uid), items: [], error: 'Failed to load' }))
      )
    );
    setAllTeamDrillGroups(results.map(r => r.status === 'fulfilled' ? r.value : { uid: '', name: '?', items: [], error: 'Failed' }));
    setAllTeamDrillLoading(false);
  };
  const openAllLocalOrders = () => openAllTeamDrill('orders', 'Orders (Emails)', 'local-orders', 'orders');
  const openAllLocalQuotes = () => openAllTeamDrill('quotes', 'Quotes Done', 'local-quotes', 'quotes');
  const openAllCRMQuotes = () => openAllTeamDrill('crm-quotes', 'CRM Quotes', 'crm-quotes', 'quotes');
  const openAllCRMProjects = () => openAllTeamDrill('crm-projects', 'CRM Projects', 'crm-projects', 'projects');

  const openCustomer = (account) => {
    setCustModalAccount(account);
    setCrmQuotesOpen(false);
    setCrmProjectsOpen(false);
    setAllTeamDrillOpen(false);
    setCustModalOpen(true);
  };

  const subHeaderLeft = (
    <>
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white/90 hover:text-white transition-colors"
        style={{ background: 'rgba(0,0,0,0.22)' }}
        data-testid="sales-hub-scroll-top-btn"
      >
        <ArrowUp className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
        <TrendingUp className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
        <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Sales Hub</span>
        {hubMode === 'my' && (
          <>
            <span className="text-white/40 font-thin select-none">|</span>
            <span className="text-white/85 font-semibold text-xs sm:text-sm whitespace-nowrap">
              {user?.display_name || user?.username || (user?.email || '').split('@')[0] || ''}
            </span>
          </>
        )}
      </div>
      {hubMode === 'team' && teams.length > 1 && (
        <div className="relative flex-shrink-0">
          <select value={selectedTeamId || ''} onChange={e => handleTeamChange(e.target.value)}
            className="appearance-none bg-white text-gray-700 text-xs font-medium rounded-full pl-3 pr-7 py-1.5 border border-gray-200 cursor-pointer shadow-sm"
            data-testid="team-selector">
            {teams.map(t => <option key={t.id} value={t.id}>{t.team_name}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none text-gray-400" />
        </div>
      )}
    </>
  );

  const subHeaderCenter = (
    <div
      className="flex items-center gap-1 rounded-full p-1 flex-shrink-0"
      style={{ background: 'rgba(0,0,0,0.18)' }}
      data-testid="hub-mode-toggle"
    >
      <button onClick={() => setHubMode('my')} data-testid="hub-mode-my"
        className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
          hubMode === 'my' ? 'bg-purple-100 text-purple-700 shadow-sm' : 'bg-white text-gray-700 shadow-sm'
        }`}>
        My Hub
      </button>
      {isManager && (
        <button onClick={() => setHubMode('team')} data-testid="hub-mode-team"
          className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
            hubMode === 'team' ? 'bg-purple-100 text-purple-700 shadow-sm' : 'bg-white text-gray-700 shadow-sm'
          }`}>
          Team Hub
        </button>
      )}
    </div>
  );

  const subHeaderRight = (
    <div className="flex items-center gap-1 relative" ref={pickerRef}>
      {['day', 'week', 'month', 'year'].map(m => (
        <button key={m} onClick={() => handleModeClick(m)} data-testid={`date-mode-${m}`}
          className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-all capitalize shadow-sm ${
            dateMode === m
              ? 'bg-purple-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}>{m}</button>
      ))}
      <button onClick={() => handleModeClick('calendar')} className="ml-0.5 p-1 rounded-full bg-white text-gray-700 shadow-sm hover:bg-gray-50">
        <Calendar className="w-3 h-3" />
      </button>
      <button onClick={() => nav(-1)} className="p-1 rounded-full bg-white/90 text-gray-700 shadow-sm hover:bg-white transition-colors" data-testid="date-nav-prev">
        <ChevronLeft className="w-3.5 h-3.5" />
      </button>
      <span
        className="bg-white/90 text-gray-800 text-[11px] px-2 py-1 rounded-full font-semibold hidden sm:inline min-w-[100px] text-center shadow-sm"
        data-testid="date-label"
      >{dateLabel()}</span>
      <button onClick={() => nav(1)} className="p-1 rounded-full bg-white/90 text-gray-700 shadow-sm hover:bg-white transition-colors" data-testid="date-nav-next">
        <ChevronRight className="w-3.5 h-3.5" />
      </button>

      {picker === 'day' && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl p-4 z-50 w-64 border border-gray-100">
          <p className="text-xs font-bold text-gray-500 uppercase mb-2">Select Day</p>
          <input type="date" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            value={pickerDate.toISOString().split('T')[0]}
            onChange={e => setPickerDate(new Date(e.target.value + 'T12:00:00'))} />
          <button onClick={() => setDay(pickerDate)} className="mt-3 w-full py-2 rounded-lg text-white text-sm font-semibold" style={{ background: ORANGE }}>Apply</button>
        </div>
      )}
      {picker === 'week' && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl p-4 z-50 w-64 border border-gray-100">
          <p className="text-xs font-bold text-gray-500 uppercase mb-2">Select Week</p>
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setPickerDate(d => subWeeks(d, 1))} className="p-1 rounded hover:bg-gray-100"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-medium">{format(startOfWeek(pickerDate, { weekStartsOn: 1 }), 'dd MMM')} – {format(endOfWeek(pickerDate, { weekStartsOn: 1 }), 'dd MMM yyyy')}</span>
            <button onClick={() => setPickerDate(d => addWeeks(d, 1))} className="p-1 rounded hover:bg-gray-100"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <button onClick={() => setWeekFrom(pickerDate)} className="w-full py-2 rounded-lg text-white text-sm font-semibold" style={{ background: ORANGE }}>Apply</button>
        </div>
      )}
      {picker === 'month' && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl p-4 z-50 w-64 border border-gray-100">
          <p className="text-xs font-bold text-gray-500 uppercase mb-2">Select Month</p>
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setPickerDate(d => subMonths(d, 1))} className="p-1 rounded hover:bg-gray-100"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-medium">{format(pickerDate, 'MMMM yyyy')}</span>
            <button onClick={() => setPickerDate(d => addMonths(d, 1))} className="p-1 rounded hover:bg-gray-100"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-1 mb-2">
            {Array.from({ length: 12 }, (_, i) => {
              const d = new Date(pickerDate.getFullYear(), i, 1);
              const isActive = d.getMonth() === pickerDate.getMonth() && d.getFullYear() === pickerDate.getFullYear();
              return (
                <button key={i} onClick={() => setMonthFrom(d)}
                  className="py-1.5 text-xs rounded-lg font-medium transition-colors"
                  style={{ background: isActive ? ORANGE : '#f3f4f6', color: isActive ? 'white' : '#374151' }}>
                  {format(d, 'MMM')}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {picker === 'calendar' && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl p-4 z-50 w-72 border border-gray-100">
          <p className="text-xs font-bold text-gray-500 uppercase mb-3">Custom Date Range</p>
          <div className="space-y-2">
            <div><label className="text-xs text-gray-500">From</label>
              <input type="date" value={calFrom} onChange={e => setCalFrom(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
            <div><label className="text-xs text-gray-500">To</label>
              <input type="date" value={calTo} onChange={e => setCalTo(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-0.5" /></div>
          </div>
          <button onClick={() => { if (calFrom && calTo) { setDateRange({ from: new Date(calFrom + 'T12:00:00'), to: new Date(calTo + 'T12:00:00') }); setDateMode('custom'); setPicker(null); } }}
            className="mt-3 w-full py-2 rounded-lg text-white text-sm font-semibold" style={{ background: ORANGE }}>Apply Range</button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <AppHeader
        user={user}
        onLogout={onLogout}
        subHeaderLeft={subHeaderLeft}
        subHeaderCenter={subHeaderCenter}
        subHeaderRight={subHeaderRight}
      />
      <div className="pt-0">

        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-6">
          {teamsLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: ORANGE }} />
            </div>
          ) : teams.length === 0 ? (
            <div className="text-center py-20 text-gray-500">No teams configured.</div>
          ) : (
            <>
              {hubMode === 'team' && isManager && tabOrder.length > 0 && (
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="member-tabs" direction="horizontal">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}
                        className="flex items-end gap-1 overflow-x-auto pb-0 border-b-2 border-gray-100 mb-6"
                        style={{ scrollbarWidth: 'none' }} data-testid="member-tabs">

                        <button onClick={() => setActiveMemberId('all')} data-testid="member-tab-all"
                          className={`flex items-center gap-1.5 px-4 py-2.5 rounded-t-xl cursor-pointer select-none whitespace-nowrap text-sm font-semibold border-t border-l border-r transition-colors ${
                            activeMemberId === 'all' ? 'text-white -mb-px' : 'text-gray-600 hover:text-gray-900 bg-gray-50 border-transparent hover:border-gray-200'
                          }`}
                          style={activeMemberId === 'all' ? { background: ORANGE, borderColor: ORANGE } : {}}>
                          <Users className="w-3.5 h-3.5" />All
                        </button>

                        {tabOrder.map((uid, index) => {
                          const isActive = uid === activeMemberId;
                          return (
                            <Draggable key={uid} draggableId={uid} index={index}>
                              {(drag, snapshot) => (
                                <div ref={drag.innerRef} {...drag.draggableProps}
                                  className={`group flex items-center gap-1.5 px-4 py-2.5 rounded-t-xl cursor-pointer select-none whitespace-nowrap text-sm font-semibold border-t border-l border-r transition-colors ${
                                    snapshot.isDragging ? 'opacity-80 shadow-lg' : ''
                                  } ${isActive ? 'text-white -mb-px' : 'text-gray-600 hover:text-gray-900 bg-gray-50 border-transparent hover:border-gray-200'}`}
                                  style={isActive ? { background: ORANGE, borderColor: ORANGE } : {}}
                                  onClick={() => setActiveMemberId(uid)} data-testid={`member-tab-${uid}`}>
                                  <div {...drag.dragHandleProps} className="opacity-0 group-hover:opacity-60 transition-opacity cursor-grab active:cursor-grabbing">
                                    <GripVertical className="w-3.5 h-3.5" />
                                  </div>
                                  <span>{memberName(uid)}</span>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}

              <div className="flex gap-4 mb-5 overflow-x-auto pb-1" data-testid="stat-boxes">
                <StatBox icon={FileText} iconColor={ORANGE} label="Quotes"
                  split={activeStats ? {
                    quotesCount: activeStats.quotes_count,
                    quotesValue: activeStats.quotes_value,
                    projectsCount: activeStats.quotes_amended_count || 0,
                    projectsValue: activeStats.quotes_amended_value || 0,
                  } : null}
                  splitLabel1="Quotes Done" splitLabel2="Quotes Amended"
                  loading={statsLoading}
                  onClickQuotes={!isAllView ? openLocalQuotes : openAllLocalQuotes} />
                <StatBox icon={Package} iconColor="#16a34a" label="Orders (Emails)"
                  ordersBreakdown={activeStats ? {
                    thisMonth: { count: activeStats.orders_count, value: activeStats.orders_value },
                    future: { count: activeStats.orders_future_count || 0, value: activeStats.orders_future_value || 0 },
                  } : null}
                  loading={statsLoading}
                  onClick={!isAllView ? openLocalOrders : openAllLocalOrders} />
                <StatBox icon={TrendingUp} iconColor="#2563eb" label="CRM Uploads"
                  split={activeStats ? {
                    quotesCount: activeStats.crm_quotes_count,
                    quotesValue: activeStats.crm_quotes_value,
                    projectsCount: activeStats.crm_projects_count,
                    projectsValue: activeStats.crm_projects_value,
                  } : null}
                  loading={statsLoading}
                  onClickQuotes={!isAllView ? openCRMQuotes : openAllCRMQuotes}
                  onClickProjects={!isAllView ? openCRMProjects : openAllCRMProjects} />
                <StatBox icon={Phone} iconColor="#0891b2" label="Calls Made"
                  count={activeStats?.calls_count}
                  loading={statsLoading}
                  onClick={!isAllView ? openCallsDetail : undefined}
                  onClickAllCalls={() => openEightX8Detail('all', isAllView ? 'All Calls (8x8) — All Team' : `All Calls (8x8) — ${activeMemberName}`)}
                  eightX8Breakdown={activeStats ? {
                    outbound: activeStats.calls_8x8_outbound_count,
                    incoming: activeStats.calls_8x8_inbound_count,
                    logged: crmLogged.loading ? '…' : (activeCrmLogged ?? '—'),
                    onOutbound: () => openEightX8Detail('outbound', 'Outbound Calls (8x8)'),
                    onIncoming: () => openEightX8Detail('inbound', 'Incoming Calls (8x8)'),
                    onLogged: openCrmLoggedDetail,
                  } : null} />
                <CRMTasksBox stats={activeStats} loading={statsLoading}
                  onClick={!isAllView ? openCRMTasks : undefined} />
              </div>

              {crmError && (
                <div className="mb-5 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
                  <span className="flex-shrink-0 mt-0.5">⚠</span>
                  <span><strong>CRM data unavailable:</strong> {crmError}</span>
                </div>
              )}

              <div className="space-y-4" data-testid="calls-section">

                <div className="flex items-center gap-1 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }} data-testid="list-tabs">
                  <button onClick={() => setActiveListTab('activity')} data-testid="list-tab-activity"
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-all ${
                      activeListTab === 'activity'
                        ? 'text-white border-transparent shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                    }`}
                    style={activeListTab === 'activity' ? { background: ORANGE } : {}}>
                    <Clock className="w-3.5 h-3.5" /> Activity
                  </button>

                  <button onClick={() => setActiveListTab('market')} data-testid="list-tab-market"
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-all ${
                      activeListTab === 'market'
                        ? 'text-white border-transparent shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                    }`}
                    style={activeListTab === 'market' ? { background: ORANGE } : {}}>
                    <BarChart3 className="w-3.5 h-3.5" /> Market Sector Calls
                  </button>

                  {!isAllView && (
                    <button onClick={() => setActiveListTab('projects')} data-testid="list-tab-projects"
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-all ${
                        activeListTab === 'projects'
                          ? 'text-white border-transparent shadow-sm'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                      }`}
                      style={activeListTab === 'projects' ? { background: ORANGE } : {}}>
                      <FolderKanban className="w-3.5 h-3.5" />
                      {hubMode === 'my' ? 'My Projects' : 'Their Projects'}
                      {myProjects.length > 0 && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeListTab === 'projects' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                          {myProjects.length}
                        </span>
                      )}
                    </button>
                  )}

                  {customLists.map(tab => (
                    <button key={tab.id} onClick={() => setActiveListTab(tab.id)} data-testid={`list-tab-${tab.id}`}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-all ${
                        activeListTab === tab.id
                          ? 'bg-sky-600 text-white border-transparent shadow-sm'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300 hover:text-sky-600'
                      }`}>
                      {tab.tab_name}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeListTab === tab.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        {(tab.items || []).length}
                      </span>
                    </button>
                  ))}

                  {isOwnFolders && (
                    <button onClick={() => setShowAddTabModal(true)} data-testid="add-tab-btn"
                      className="flex items-center gap-1 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap bg-white border border-dashed border-gray-300 text-gray-400 hover:border-orange-400 hover:text-orange-500 transition-colors">
                      <Plus className="w-3.5 h-3.5" /> Add Folder
                    </button>
                  )}
                </div>

                {activeListTab === 'activity' && (
                  <SalesActivityHub
                    user={user}
                    hubMode={hubMode}
                    effectiveMemberId={effectiveMemberId}
                    effectiveMemberName={effectiveMemberName}
                    isAllView={isAllView}
                    tabOrder={tabOrder}
                    memberName={memberName}
                    dateMode={dateMode}
                    dateRange={dateRange}
                    authHeaders={authHeaders}
                    API={API}
                    calls={calls}
                    onOpenCustomer={openCustomer}
                    onEditCall={(call) => setCallsDetailEditingCall(call)}
                    onOpenCall={(call) => setAddCallItem({
                      account_id: call.account_id,
                      account_name: call.account_name,
                      contact_id: call.contact_id,
                      contact_name: call.contact_name,
                      business_unit: call.business_unit,
                      call_purpose: call.purpose || (call.direction === 'inbound' ? 'Price Enquiry' : 'Cold Call'),
                      direction: call.direction === 'inbound' ? 'incoming' : 'outgoing',
                      description: call.notes || '',
                    })}
                    onOpenLocalItem={(item) => setParsedOrderView(item)}
                    onOpenTask={(task) => setActivityTaskModal(task)}
                  />
                )}

                {activeListTab === 'market' && (<>

                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" style={{ color: ORANGE }} />
                    <h2 className="font-bold text-gray-800 text-sm">Market Sector Calls</h2>
                    {callsFetched && (
                      <span className="text-xs text-gray-400">{filteredCalls.length} call{filteredCalls.length !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {callsFetched && (
                      <button
                        onClick={() => setScheduledOnly(v => !v)}
                        data-testid="scheduled-calls-toggle"
                        title={isAllView ? "Show every team member's scheduled calls, grouped by user" : 'Show only scheduled (not-yet-completed) calls for the selected period'}
                        className={`flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-bold border transition-colors ${
                          scheduledOnly ? 'text-white border-transparent' : 'bg-white text-gray-500 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                        }`}
                        style={scheduledOnly ? { background: ORANGE } : {}}
                      >
                        <Calendar className="w-3.5 h-3.5" />
                        Scheduled Only
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${scheduledOnly ? 'bg-white/20 text-white' : 'bg-orange-50 text-orange-600'}`}>
                          {scheduledCallsCount}
                        </span>
                      </button>
                    )}
                    <button
                      onClick={fetchCalls}
                      disabled={callsLoading || !selectedTeamId}
                      className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-white text-xs font-semibold disabled:opacity-50 transition-opacity"
                      style={{ background: ORANGE }}
                      data-testid="load-calls-btn"
                    >
                      {callsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      Refresh
                    </button>
                  </div>
                </div>

                {isManager && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {callsFetched && filteredCalls.length > 0 && (
                      <button
                        onClick={() => setShowExport(true)}
                        className="flex items-center gap-1.5 h-8 px-3 rounded-xl border border-sky-200 text-sky-700 hover:bg-sky-50 text-xs font-semibold transition-colors"
                        data-testid="market-export-btn"
                      >
                        <Download className="w-3.5 h-3.5" /> Export
                      </button>
                    )}
                    {callsFetched && memberFilteredCalls.length > 0 && (
                      <button
                        onClick={() => setShowCrmReport(true)}
                        className="flex items-center gap-1.5 h-8 px-3 rounded-xl text-white text-xs font-semibold"
                        style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
                        data-testid="market-gen-report-btn"
                      >
                        <Sparkles className="w-3.5 h-3.5" /> Generate Sales Report
                      </button>
                    )}
                  </div>

                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setPhocasExpanded(v => !v)}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                        data-testid="phocas-data-toggle"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-gray-700">Phocas Data</span>
                          {phocasData && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">
                              Loaded
                            </span>
                          )}
                        </div>
                        <ChevronDown
                          className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${phocasExpanded ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {phocasExpanded && (
                        <div className="px-3 py-3 border-t border-gray-200 bg-white space-y-3">
                          <PhocasUploadZone
                            onParsed={handlePhocasParsed}
                            currentData={phocasData}
                            selectedPeriodLabel={phocasPeriodLabel}
                          />
                          {phocasData?.phocasSectors && Object.keys(phocasData.phocasSectors).length > 0 && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-gray-400 font-medium">View:</span>
                              <button onClick={() => setCallsOnly(false)}
                                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                                  !callsOnly ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-500 border-gray-200 hover:border-sky-300 hover:text-sky-600'
                                }`} data-testid="phocas-mode-all">All Data</button>
                              <button onClick={() => setCallsOnly(true)}
                                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                                  callsOnly ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700'
                                }`} data-testid="phocas-mode-calls">Calls Only</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {callsFetched && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-400 mr-1">
                        <Building2 className="w-3.5 h-3.5 inline mr-0.5" /> BU
                      </span>
                      <Chip label="All" active={buFilters.size === 0} onClick={() => setBuFilters(new Set())} testId="bu-filter-all" />
                      {BUS.map(bu => (
                        <Chip key={bu} label={`${bu} (${buCounts[bu]})`} active={buFilters.has(bu)}
                          onClick={() => toggleBu(bu)} testId={`bu-filter-${bu.toLowerCase()}`} />
                      ))}
                    </div>
                    <div className="w-px h-6 bg-gray-200 hidden sm:block" />
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-400 mr-1">Direction</span>
                      <Chip label="All" active={dirFilters.size === 0} onClick={() => setDirFilters(new Set())} testId="dir-filter-all" />
                      <Chip label={`Outgoing (${outgoing})`} active={dirFilters.has('outgoing')} onClick={() => toggleDir('outgoing')} testId="dir-filter-outgoing" color="orange" />
                      <Chip label={`Incoming (${incoming})`} active={dirFilters.has('incoming')} onClick={() => toggleDir('incoming')} testId="dir-filter-incoming" />
                    </div>
                  </div>
                )}

                {callsFetched && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-xl p-3 text-center bg-gray-900 text-white" data-testid="stat-total-calls">
                      <p className="text-2xl font-black">{filteredCalls.length}</p>
                      <p className="text-xs font-semibold opacity-80 mt-0.5">Total Calls</p>
                      {phocasData?.byName && (
                        <div className="mt-2 pt-2 border-t border-white/20 space-y-0.5">
                          <p className="text-[11px] font-bold text-sky-300">
                            {totalStats.phocasTotal >= 1 ? `£${Math.round(totalStats.phocasTotal).toLocaleString('en-GB')}` : '—'}
                          </p>
                          <p className="text-[9px] uppercase tracking-wider opacity-60">Phocas Sales</p>
                        </div>
                      )}
                      {quotesData && (
                        <div className="mt-1.5 space-y-0.5">
                          <p className="text-[11px] font-bold text-emerald-300">
                            {totalStats.quotesCount} q · £{Math.round(totalStats.quotesValue).toLocaleString('en-GB')}
                          </p>
                          <p className="text-[9px] uppercase tracking-wider opacity-60">Unique Quoted Accts</p>
                        </div>
                      )}
                    </div>
                    {BUS.map(bu => {
                      const isActive = buFilters.has(bu);
                      const bs = buStats[bu] || {};
                      return (
                        <button key={bu} type="button" onClick={() => toggleBu(bu)}
                          className={`rounded-xl p-3 text-center transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98] ${
                            isActive ? 'bg-sky-600 text-white shadow-md' : 'bg-sky-50 text-sky-800 border border-sky-200 hover:bg-sky-100'
                          }`}
                          data-testid={`stat-${bu.toLowerCase()}`}>
                          <p className="text-2xl font-black">{buCounts[bu]}</p>
                          <p className="text-xs font-semibold opacity-80 mt-0.5">{bu}</p>
                          {phocasData?.byName && (
                            <div className={`mt-2 pt-2 border-t ${isActive ? 'border-white/20' : 'border-sky-200'} space-y-0.5`}>
                              <p className={`text-[11px] font-bold ${isActive ? 'text-sky-200' : 'text-sky-600'}`}>
                                {bs.phocasTotal >= 1 ? `£${Math.round(bs.phocasTotal).toLocaleString('en-GB')}` : '—'}
                              </p>
                              <p className="text-[9px] uppercase tracking-wider opacity-60">Phocas Sales</p>
                            </div>
                          )}
                          {quotesData && (
                            <div className="mt-1.5 space-y-0.5">
                              <p className={`text-[11px] font-bold ${isActive ? 'text-emerald-200' : 'text-emerald-600'}`}>
                                {bs.quotesCount} q · £{Math.round(bs.quotesValue).toLocaleString('en-GB')}
                              </p>
                              <p className="text-[9px] uppercase tracking-wider opacity-60">Quotes</p>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {callsLoading && (
                  <div className="py-20 text-center text-gray-400">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: ORANGE }} />
                    <p className="text-sm">Fetching calls from Zoho…</p>
                  </div>
                )}
                {!callsLoading && !callsFetched && (
                  <div className="py-16 text-center text-gray-400">
                    <Phone className="w-9 h-9 mx-auto mb-3 text-gray-200" />
                    <p className="text-sm">Click <strong>Load Calls</strong> to fetch call activity for the selected period.</p>
                  </div>
                )}
                {!callsLoading && callsFetched && filteredCalls.length === 0 && (
                  <p className="py-14 text-center text-sm text-gray-400">No calls found for this period.</p>
                )}
                {!callsLoading && callsFetched && owners.map(owner => (
                  <OwnerGroup
                    key={owner}
                    ownerName={owner}
                    calls={byOwner[owner]}
                    quotesData={quotesData}
                    phocasNormLookup={phocasData?.normLookup || null}
                    phocasPrevNormLookup={phocasData?.prevNormLookup || null}
                    phocasPeriodLabel={phocasPeriodLabel}
                    phocasPrevPeriodLabel={phocasPrevPeriodLabel}
                    selectedIds={selectedAccountIdsSet}
                    onToggleSelect={toggleAccountSelect}
                  />
                ))}
                </>)}

                {activeListTab !== 'market' && activeListTab !== 'projects' && activeCustomList && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-sky-50/50">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-gray-800 text-sm">{activeCustomList.tab_name}</h3>
                        <span className="text-xs bg-sky-100 text-sky-700 font-semibold px-2 py-0.5 rounded-full">
                          {(activeCustomList.items || []).length} account{(activeCustomList.items || []).length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isOwnFolders && !activeCustomList.merged && (
                          <>
                            <button onClick={() => setShowAddItemModal(true)} data-testid="add-account-btn"
                              className="flex items-center gap-1.5 h-8 px-3 rounded-xl text-white text-xs font-semibold"
                              style={{ background: ORANGE }}>
                              <Plus className="w-3.5 h-3.5" /> Add Account
                            </button>
                            <button onClick={() => handleDeleteTab(activeListTab)} data-testid="delete-tab-btn"
                              className="flex items-center gap-1.5 h-8 px-3 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 text-xs font-semibold transition-colors">
                              <Trash2 className="w-3.5 h-3.5" /> Delete Folder
                            </button>
                          </>
                        )}
                        {activeCustomList.merged && (
                          <span className="text-[11px] text-sky-600 font-semibold bg-sky-100 px-2 py-1 rounded-full">
                            Combined from everyone's "{activeCustomList.tab_name}" folder
                          </span>
                        )}
                      </div>
                    </div>

                    {(activeCustomList.items || []).length > 0 && (
                      <div className="grid gap-3 px-3 py-2 border-b border-gray-100 bg-gray-50/80" style={{ gridTemplateColumns: '1.8fr 0.65fr 1.2fr 1.6fr' }}>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Account / Contact</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">BU</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Phocas</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Notes</span>
                      </div>
                    )}

                    {(activeCustomList.items || []).length === 0 ? (
                      <div className="py-16 text-center text-gray-400">
                        <Phone className="w-9 h-9 mx-auto mb-3 text-gray-200" />
                        <p className="text-sm">No accounts yet.</p>
                        {isOwnFolders && !activeCustomList.merged && (
                          <button onClick={() => setShowAddItemModal(true)}
                            className="mt-3 px-4 py-2 rounded-xl text-white text-xs font-semibold"
                            style={{ background: ORANGE }}>
                            Add First Account
                          </button>
                        )}
                      </div>
                    ) : (
                      (activeCustomList.items || []).map(item => (
                        <CustomListRow
                          key={item.id}
                          tabId={activeListTab}
                          item={item}
                          phocasNormLookup={phocasData?.normLookup || null}
                          phocasPrevNormLookup={phocasData?.prevNormLookup || null}
                          phocasPeriodLabel={phocasPeriodLabel}
                          phocasPrevPeriodLabel={phocasPrevPeriodLabel}
                          canEdit={isOwnFolders && !activeCustomList.merged}
                          callStatus={callStatusByAccount[item.account_id] || null}
                          onUpdateItem={(itemId, patch) => handleUpdateItem(activeListTab, itemId, patch)}
                          onRemove={(itemId) => handleRemoveItem(activeListTab, itemId)}
                          onOpenCustomer={setCustomerModalName}
                          onAddCall={setAddCallItem}
                          selected={!!selectedAccounts[item.account_id]}
                          onToggleSelect={toggleAccountSelect}
                        />
                      ))
                    )}
                  </div>
                )}

                {activeListTab === 'projects' && !isAllView && (
                  <div data-testid="my-projects-section">
                    <MyProjectsHubView
                      projects={myProjects}
                      user={user}
                      hubMode={hubMode}
                      targetUserId={activeMemberId !== 'all' ? activeMemberId : user.id}
                      isManager={isManager}
                      onProjectRemoved={(projectId) => {
                        setMyProjects(prev => prev.filter(p => p.project_id !== projectId));
                      }}
                      onRefresh={() => fetchMyProjects(hubMode === 'my' ? user.id : activeMemberId)}
                      loading={myProjectsLoading}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {selectedAccountsList.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10001] flex items-center gap-3 bg-gray-900 text-white rounded-2xl shadow-2xl px-5 py-3"
          data-testid="bulk-select-action-bar">
          <span className="text-sm font-semibold">{selectedAccountsList.length} customer{selectedAccountsList.length !== 1 ? 's' : ''} selected</span>
          <button onClick={() => setShowBulkScheduleModal(true)} data-testid="bulk-schedule-calls-btn"
            className="flex items-center gap-1.5 h-9 px-4 rounded-xl text-white text-xs font-bold"
            style={{ background: ORANGE }}>
            <Phone className="w-3.5 h-3.5" /> Schedule Calls
          </button>
          <button onClick={clearAccountSelection} data-testid="bulk-clear-selection-btn"
            className="text-xs text-gray-300 hover:text-white underline">
            Clear
          </button>
        </div>
      )}

      {showExport && <ExportModal calls={filteredCalls} onClose={() => setShowExport(false)} />}
      {quotesModal && (
        <QuotesDetailModal
          accountName={quotesModal.accountName}
          quotes={quotesModal.quotes}
          onClose={() => setQuotesModal(null)}
        />
      )}
      <ContactNotesTasksModal
        isOpen={!!customerModalName}
        onClose={() => setCustomerModalName(null)}
        initialSearch={customerModalName}
      />
      <CrmSalesReportModal
        isOpen={showCrmReport}
        onClose={() => setShowCrmReport(false)}
        calls={memberFilteredCalls}
        quotesData={quotesData}
        phocasData={phocasData}
        startDate={toDateStr(dateRange.from)}
        endDate={toDateStr(dateRange.to)}
        teamName={teamName}
        buTotals={buTotals}
        callsOnly={callsOnly}
        phocasSectors={phocasData?.phocasSectors || {}}
        onCreated={handleReportCreated}
      />
      {selectedReport && (
        <TeamReportViewerModal
          isOpen={!!selectedReport}
          reportSummary={selectedReport}
          onClose={() => setSelectedReport(null)}
        />
      )}
      {showAddTabModal && (
        <AddTabModal onClose={() => setShowAddTabModal(false)} onCreate={handleCreateTab} />
      )}
      {showAddItemModal && activeListTab !== 'market' && activeListTab !== 'projects' && (
        <AddAccountModal
          onClose={() => setShowAddItemModal(false)}
          onAdd={handleAddItem}
          existingIds={activeListExistingIds}
        />
      )}
      <BulkScheduleCallModal
        isOpen={showBulkScheduleModal}
        onClose={() => setShowBulkScheduleModal(false)}
        selectedItems={selectedAccountsList}
        onScheduled={() => { clearAccountSelection(); setShowBulkScheduleModal(false); }}
      />

      {addCallItem && (
        <AddCallToCrmModal
          isOpen={!!addCallItem}
          onClose={() => setAddCallItem(null)}
          prefillAccount={{
            account: addCallItem.account_id ? { id: addCallItem.account_id, name: addCallItem.account_name } : null,
            contact: addCallItem.contact_id ? { id: addCallItem.contact_id, name: addCallItem.contact_name } : undefined,
            call_purpose: addCallItem.call_purpose || undefined,
            business_unit: addCallItem.business_unit || undefined,
            direction: addCallItem.direction || undefined,
            description: addCallItem.description || undefined,
          }}
          onCompleted={() => { setAddCallItem(null); fetchCallStatus(); }}
        />
      )}
      {activityTaskModal && (
        <TaskDetailsModal
          task={activityTaskModal}
          isCompleted={activityTaskModal.status === 'Completed'}
          onClose={() => setActivityTaskModal(null)}
          onComplete={() => setActivityTaskModal(null)}
          onSaved={() => setActivityTaskModal(null)}
          onReactivated={() => setActivityTaskModal(null)}
        />
      )}

      <CRMQuotesModal isOpen={crmQuotesOpen} onClose={() => setCrmQuotesOpen(false)}
        quotes={crmDetailItems} loading={crmDetailLoading} error={crmDetailError} onOpenAccount={openCustomer} />
      <CRMProjectsModal isOpen={crmProjectsOpen} onClose={() => setCrmProjectsOpen(false)}
        projects={crmDetailItems} loading={crmDetailLoading} error={crmDetailError} onOpenAccount={openCustomer} />
      <CRMTasksModal
        isOpen={crmTasksOpen}
        onClose={() => { setCrmTasksOpen(false); setCrmTasksItems([]); }}
        tasks={crmTasksItems}
        loading={crmTasksLoading}
        error={crmTasksError}
        memberName={effectiveMemberName}
        dateLabel={dateLabel()}
      />
      <CallsDetailModal
        isOpen={callsDetailOpen}
        onClose={() => { setCallsDetailOpen(false); setCallsDetailItems([]); }}
        calls={callsDetailItems}
        loading={callsDetailLoading}
        error={callsDetailError}
        memberName={effectiveMemberName}
        dateLabel={dateLabel()}
        onEditCall={(call) => {
          setCallsDetailEditingCall(call);
        }}
      />
      {callsDetailEditingCall && (
        <AddCallToCrmModal
          isOpen={!!callsDetailEditingCall}
          onClose={() => setCallsDetailEditingCall(null)}
          editCall={{
            id: callsDetailEditingCall.id,
            account: callsDetailEditingCall.account_id
              ? { id: callsDetailEditingCall.account_id, name: callsDetailEditingCall.account_name }
              : null,
            contact: callsDetailEditingCall.contact_id
              ? { id: callsDetailEditingCall.contact_id, name: callsDetailEditingCall.contact_name }
              : null,
            call_purpose: callsDetailEditingCall.purpose,
            description: callsDetailEditingCall.description,
            call_start_time: callsDetailEditingCall.start_time,
            call_type: callsDetailEditingCall.call_type,
            business_unit: callsDetailEditingCall.business_unit,
            call_url: callsDetailEditingCall.crm_url,
          }}
          onSaved={() => {
            setCallsDetailEditingCall(null);
            window.dispatchEvent(new Event('c30:calls-changed'));
          }}
          onDeleted={() => {
            setCallsDetailEditingCall(null);
            window.dispatchEvent(new Event('c30:calls-changed'));
          }}
        />
      )}
      <EightX8HistoryModal
        isOpen={eightX8DetailOpen}
        onClose={() => { setEightX8DetailOpen(false); setEightX8DetailItems([]); }}
        title={eightX8DetailTitle}
        calls={eightX8DetailItems}
        loading={eightX8DetailLoading}
        error={eightX8DetailError}
        showUser={isAllView}
        onOpenAccount={openCustomer}
      />
      <LocalDrillModal
        isOpen={localOrdersOpen}
        onClose={() => { setLocalOrdersOpen(false); setLocalDrillItems([]); setDebugInfo(null); }}
        title="Orders (Emails)"
        items={localDrillItems}
        loading={localDrillLoading}
        error={localDrillError}
        isOrders={true}
        refDate={dateRange.to}
        onViewItem={(item) => { setLocalOrdersOpen(false); setParsedOrderView(item); }}
        debugInfo={user?.role === 'super_admin' ? debugInfo : null}
      />
      <LocalDrillModal
        isOpen={localQuotesOpen}
        onClose={() => { setLocalQuotesOpen(false); setLocalDrillItems([]); }}
        title="Quotes Done"
        items={localDrillItems}
        loading={localDrillLoading}
        error={localDrillError}
        isOrders={false}
        onViewItem={(item) => { setLocalQuotesOpen(false); setParsedOrderView(item); }}
      />
      <AllTeamDrillModal
        isOpen={allTeamDrillOpen}
        onClose={() => { setAllTeamDrillOpen(false); setAllTeamDrillGroups([]); }}
        type={allTeamDrillType}
        title={allTeamDrillTitle}
        userGroups={allTeamDrillGroups}
        loading={allTeamDrillLoading}
        refDate={dateRange.to}
        onViewItem={(item) => { setAllTeamDrillOpen(false); setParsedOrderView(item); }}
        onOpenAccount={openCustomer}
      />
      {parsedOrderView && (
        <ParsedOrderViewModal
          order={parsedOrderView}
          onClose={() => setParsedOrderView(null)}
        />
      )}
      <ContactNotesTasksModal
        isOpen={custModalOpen}
        onClose={() => { setCustModalOpen(false); setCustModalAccount(null); }}
        initialAccount={custModalAccount}
      />
    </div>
  );
}
